import { useState, useEffect, useCallback, useMemo } from "react";
import Papa from "papaparse";
import { useClub } from "@/pages/ClubLayout";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Medal, Plus, X, ArrowLeft, Users, Clock, ClipboardList, BarChart3,
  Trash2, Pencil, Calendar, Trophy, CheckCircle2, Image as ImageIcon,
  Upload, MapPin, FileText, Utensils, PartyPopper, Award, Car, Info, Flag, Globe, Star, Search, ChevronDown,
} from "lucide-react";
import { ConfirmDeleteModal } from "@/components/ConfirmDeleteModal";
import PuttingTab from "@/components/PuttingTab";
import CalendarCsvImport from "@/components/CalendarCsvImport";
import TeeTimeCsvImport from "@/components/TeeTimeCsvImport";
import NextRoundPanel, { RoundProgressBadges } from "@/components/NextRoundPanel";
import MarshallCapture from "@/components/MarshallCapture";
import PublishDayPanel from "@/components/PublishDayPanel";
import ScorecardModal from "@/components/ScorecardModal";
import AGoGoManager from "@/components/AGoGoManager";

const AUTH = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("cadii_club_token") || ""}`,
});

const STATUS_LABEL = {
  draft: { txt: "Borrador", cls: "bg-gray-100 text-gray-700" },
  open: { txt: "Abierto", cls: "bg-blue-100 text-blue-700" },
  in_progress: { txt: "En Juego", cls: "bg-green-100 text-green-700" },
  completed: { txt: "Finalizado", cls: "bg-purple-100 text-purple-700" },
};

const HCP_PRESETS = [100, 95, 90, 85, 80, 75];
const DEFAULT_PAR = [4, 4, 3, 5, 4, 3, 4, 5, 4, 4, 3, 4, 5, 4, 4, 5, 3, 4];
const TIEBREAK_OPTIONS = [
  { key: "last18", label: "Últimos 18" },
  { key: "last9", label: "Últimos 9" },
  { key: "last6", label: "Últimos 6" },
  { key: "last3", label: "Últimos 3" },
  { key: "last1", label: "Último hoyo" },
];
const TIEBREAK_LABEL = Object.fromEntries(TIEBREAK_OPTIONS.map((o) => [o.key, o.label]));

const DEFAULT_STABLEFORD_TABLE = [
  { key: "le_minus4", label: "4+ bajo par", points: 6 },
  { key: "minus3", label: "Albatross (3 bajo par)", points: 5 },
  { key: "minus2", label: "Eagle (2 bajo par)", points: 4 },
  { key: "minus1", label: "Birdie (1 bajo par)", points: 3 },
  { key: "par", label: "Par / Net Par", points: 2 },
  { key: "plus1", label: "Bogey (1 sobre par)", points: 1 },
  { key: "ge_plus2", label: "Doble Bogey o peor (2+ sobre par)", points: 0 },
];

export default function ClubTournaments() {
  const ctx = useClub();
  const canWrite = ["master", "operador"].includes(ctx?.clubUser?.club_role || "");
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const res = await fetch(`${API}/club/tournaments`, { credentials: "include", headers: AUTH() });
      if (res.ok) setTournaments(await res.json());
    } catch { toast.error("Error al cargar"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (selectedId) {
    return (
      <TournamentDetail
        tournamentId={selectedId}
        canWrite={canWrite}
        onBack={() => { setSelectedId(null); fetchAll(); }}
      />
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A] flex items-center gap-2" data-testid="tournaments-title">
            <Medal className="w-6 h-6 text-[#1B4D3E]" strokeWidth={1.5} /> Torneos
          </h1>
          <p className="text-sm text-[#64748B] mt-1">{ctx?.club?.name}</p>
        </div>
        {canWrite && (
          <Button onClick={() => setShowCreate(true)} className="bg-[#1B4D3E] hover:bg-[#163F33] text-white" data-testid="new-tournament-btn">
            <Plus className="w-4 h-4 mr-2" /> Nuevo Torneo
          </Button>
        )}
      </div>

      {showCreate && (
        <TournamentForm onClose={() => setShowCreate(false)} onSaved={(t) => { setShowCreate(false); fetchAll(); setSelectedId(t.tournament_id); }} />
      )}

      {loading ? (
        <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-[#1B4D3E] border-t-transparent rounded-full animate-spin" /></div>
      ) : tournaments.length === 0 ? (
        <Card className="card-default">
          <CardContent className="py-16 text-center">
            <Medal className="w-12 h-12 text-[#64748B] mx-auto mb-4 opacity-40" />
            <p className="text-[#64748B] font-medium mb-1">No hay torneos creados</p>
            <p className="text-sm text-[#94A3B8] mb-4">Crea tu primer torneo desde cero</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="tournaments-list">
          {tournaments.map((t) => {
            const st = STATUS_LABEL[t.status] || STATUS_LABEL.draft;
            return (
              <Card key={t.tournament_id} className="card-default hover:shadow-md transition-shadow cursor-pointer" onClick={() => setSelectedId(t.tournament_id)} data-testid={`tournament-${t.tournament_id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-bold text-[#0F172A]">{t.name}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${st.cls}`}>{st.txt}</span>
                  </div>
                  <div className="flex flex-wrap gap-3 text-xs text-[#64748B]">
                    {t.start_date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {t.start_date}</span>}
                    <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {t.player_count || 0}</span>
                    <span className="flex items-center gap-1">{t.rounds} ronda{t.rounds > 1 ? "s" : ""}</span>
                    <span className="bg-[#1B4D3E]/10 text-[#1B4D3E] px-1.5 py-0.5 rounded text-xs font-medium">{t.mode === "net" ? `NET ${t.hcp_percent}%` : "GROSS"}</span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ==========================================================================
// Tournament Form (Create / Edit)
// ==========================================================================

function TournamentForm({ tournament, onClose, onSaved }) {
  const { club } = useClub();
  const [categories, setCategories] = useState([]);
  const [courses, setCourses] = useState([]);
  const [form, setForm] = useState({
    name: tournament?.name || "",
    course_id: tournament?.course_id || "",
    course_name: tournament?.course_name || "",
    start_date: tournament?.start_date || "",
    end_date: tournament?.end_date || "",
    rounds: tournament?.rounds || 1,
    mode: tournament?.mode || "gross",
    scoring_format: tournament?.scoring_format || "medal_play",
    hcp_percent: tournament?.hcp_percent || 100,
    hcp_custom: false,
    par_holes: tournament?.par_holes || DEFAULT_PAR,
    stableford_table: (tournament?.stableford_table?.length ? tournament.stableford_table : DEFAULT_STABLEFORD_TABLE).map((r) => ({ ...r })),
    category_ids: tournament?.categories?.map((c) => c.category_id) || [],
    category_configs: (tournament?.categories || []).reduce((acc, c) => {
      acc[c.category_id] = {
        rounds: c.rounds || tournament?.rounds || 1,
        mode: c.mode || tournament?.mode || "gross",
        scoring_format: c.scoring_format || tournament?.scoring_format || "medal_play",
        hcp_percent: c.hcp_percent || tournament?.hcp_percent || 100,
        tee: c.tee || "",
        playing_days: c.playing_days || [],
        cut: c.cut || null,
      };
      return acc;
    }, {}),
    // Category-assignment wizard state.
    // mode: "range" | "sequential" | "mixed"
    // rules: { [category_id]: { type: "RANGE"|"SEQUENTIAL_BLOCK", block_size?: 16 } }
    category_assignment: tournament?.category_assignment || { mode: "range", rules: {} },
    notes: tournament?.notes || "",
    tiebreaker: (tournament?.tiebreaker?.length ? tournament.tiebreaker : ["last9", "last6", "last3", "last1"]).slice(),
  });
  const [saving, setSaving] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [courseMenuOpen, setCourseMenuOpen] = useState(false);
  const [courseQuery, setCourseQuery] = useState("");

  useEffect(() => {
    fetch(`${API}/club/categories`, { credentials: "include", headers: AUTH() })
      .then((r) => r.ok ? r.json() : [])
      .then(setCategories)
      .catch(() => {});
    fetch(`${API}/courses${club?.club_id ? `?club_id=${club.club_id}` : ""}`, { credentials: "include", headers: AUTH() })
      .then((r) => r.ok ? r.json() : [])
      .then(setCourses)
      .catch(() => {});
    fetch(`${API}/club/favorite-courses`, { credentials: "include", headers: AUTH() })
      .then((r) => r.ok ? r.json() : { favorite_course_ids: [] })
      .then((d) => setFavorites(d.favorite_course_ids || []))
      .catch(() => {});
  }, [club?.club_id]);

  const toggleFav = async (e, courseId) => {
    e.stopPropagation();
    setFavorites((p) => p.includes(courseId) ? p.filter((x) => x !== courseId) : [...p, courseId]);
    try {
      const res = await fetch(`${API}/club/favorite-courses/${courseId}/toggle`, { method: "POST", credentials: "include", headers: AUTH() });
      const d = await res.json();
      if (res.ok) setFavorites(d.favorite_course_ids || []);
    } catch { /* ignore */ }
  };

  const handleCourseChange = async (courseId) => {
    if (courseId === "other") {
      setForm((p) => ({ ...p, course_id: "", course_name: "" }));
      return;
    }
    const course = courses.find((c) => c.id === courseId);
    if (!course) return;
    // Fetch holes and pre-fill par
    try {
      const res = await fetch(`${API}/courses/${courseId}/holes`, { credentials: "include", headers: AUTH() });
      if (res.ok) {
        const holes = await res.json();
        const par = holes.map((h) => h.par);
        setForm((p) => ({
          ...p,
          course_id: courseId,
          course_name: course.name,
          par_holes: par.length === 18 ? par : p.par_holes,
        }));
        toast.success(`Campo ${course.name} cargado · Par ${par.reduce((a, b) => a + b, 0)}`);
      }
    } catch {
      setForm((p) => ({ ...p, course_id: courseId, course_name: course.name }));
    }
  };

  const toggleCat = (cid) => setForm((p) => {
    const inList = p.category_ids.includes(cid);
    if (inList) {
      const nextCfg = { ...p.category_configs };
      delete nextCfg[cid];
      const nextRules = { ...(p.category_assignment?.rules || {}) };
      delete nextRules[cid];
      return {
        ...p,
        category_ids: p.category_ids.filter((x) => x !== cid),
        category_configs: nextCfg,
        category_assignment: { ...p.category_assignment, rules: nextRules },
      };
    }
    // New category: seed rule with the type derived from the current mode.
    const mode = p.category_assignment?.mode || "range";
    const defaultType = mode === "sequential" ? "SEQUENTIAL_BLOCK" : "RANGE";
    const newRule = defaultType === "SEQUENTIAL_BLOCK" ? { type: "SEQUENTIAL_BLOCK", block_size: 16 } : { type: "RANGE" };
    return {
      ...p,
      category_ids: [...p.category_ids, cid],
      category_configs: {
        ...p.category_configs,
        [cid]: { rounds: 1, mode: "gross", scoring_format: "medal_play", hcp_percent: 100, tee: "", playing_days: [] },
      },
      category_assignment: {
        ...p.category_assignment,
        rules: { ...(p.category_assignment?.rules || {}), [cid]: newRule },
      },
    };
  });

  const setAssignMode = (mode) => setForm((p) => {
    // When switching modes, re-seed each selected category's rule so the UI
    // reflects the mode without erasing the user's block_size preferences.
    const rules = { ...(p.category_assignment?.rules || {}) };
    for (const cid of p.category_ids) {
      const cur = rules[cid] || {};
      if (mode === "range") {
        rules[cid] = { type: "RANGE" };
      } else if (mode === "sequential") {
        rules[cid] = { type: "SEQUENTIAL_BLOCK", block_size: cur.block_size || 16 };
      } else {
        // mixed: keep whatever type each category already has, default RANGE.
        rules[cid] = cur.type ? cur : { type: "RANGE" };
      }
    }
    return { ...p, category_assignment: { ...p.category_assignment, mode, rules } };
  });

  const setCatRule = (cid, patch) => setForm((p) => {
    const rules = { ...(p.category_assignment?.rules || {}) };
    rules[cid] = { ...(rules[cid] || { type: "RANGE" }), ...patch };
    return { ...p, category_assignment: { ...p.category_assignment, rules } };
  });

  const updateCatCfg = (cid, patch) => setForm((p) => ({
    ...p,
    category_configs: {
      ...p.category_configs,
      [cid]: { ...(p.category_configs[cid] || {}), ...patch },
    },
  }));

  const updateCatPlayingDay = (cid, idx, value) => setForm((p) => {
    const cur = p.category_configs[cid] || { playing_days: [] };
    const days = [...(cur.playing_days || [])];
    days[idx] = value;
    return { ...p, category_configs: { ...p.category_configs, [cid]: { ...cur, playing_days: days } } };
  });

  const updPar = (i, v) => {
    const par = [...form.par_holes];
    par[i] = parseInt(v) || 4;
    setForm({ ...form, par_holes: par });
  };

  const setStablefordPoints = (key, value) => setForm((p) => ({
    ...p,
    stableford_table: p.stableford_table.map((r) =>
      r.key === key ? { ...r, points: Math.max(0, Math.min(20, parseInt(value) || 0)) } : r
    ),
  }));

  const resetStablefordTable = () => setForm((p) => ({
    ...p,
    stableford_table: DEFAULT_STABLEFORD_TABLE.map((r) => ({ ...r })),
  }));

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error("Nombre requerido");
    setSaving(true);
    try {
      const url = tournament ? `${API}/club/tournaments/${tournament.tournament_id}` : `${API}/club/tournaments`;
      const method = tournament ? "PUT" : "POST";
      const payload = {
        name: form.name,
        scoring_format: form.scoring_format,
        course_id: form.course_id || null,
        course_name: form.course_name || null,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        par_holes: form.par_holes.map((p) => parseInt(p) || 4),
        stableford_table: form.stableford_table,
        category_ids: form.scoring_format === "a_go_go" ? [] : form.category_ids,
        category_configs: form.scoring_format === "a_go_go" ? {} : form.category_configs,
        category_assignment: form.scoring_format === "a_go_go" ? null : form.category_assignment,
        notes: form.notes || null,
        tiebreaker: form.tiebreaker,
      };
      const res = await fetch(url, { method, credentials: "include", headers: AUTH(), body: JSON.stringify(payload) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Error");
      toast.success(tournament ? "Torneo actualizado" : "Torneo creado");
      onSaved(data);
    } catch (err) {
      toast.error(typeof err.message === "string" ? err.message : "Error");
    } finally { setSaving(false); }
  };

  const parTotal = form.par_holes.reduce((a, b) => a + (parseInt(b) || 0), 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="tournament-form">
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
          <h3 className="font-bold text-[#0F172A]">{tournament ? "Editar Torneo" : "Nuevo Torneo"}</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-[#64748B]" /></button>
        </div>
        <div className="p-6 space-y-5">
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Nombre del Torneo *</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ej: Copa Aniversario 2026" data-testid="trn-name" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Tipo de Torneo</Label>
            <div className="grid grid-cols-2 gap-2">
              {[{ k: "individual", l: "Individual" }, { k: "a_go_go", l: "A Go Go (equipos)" }].map((o) => {
                const active = (o.k === "a_go_go") === (form.scoring_format === "a_go_go");
                return (
                  <button key={o.k} type="button"
                    onClick={() => setForm((p) => ({ ...p, scoring_format: o.k === "a_go_go" ? "a_go_go" : (p.scoring_format === "a_go_go" ? "medal_play" : p.scoring_format) }))}
                    className={`h-10 rounded-md border text-sm font-semibold ${active ? "border-[#1B4D3E] bg-[#1B4D3E]/10 text-[#1B4D3E]" : "border-gray-200 text-[#64748B]"}`}
                    data-testid={`trn-type-${o.k}`}>
                    {o.l}
                  </button>
                );
              })}
            </div>
            {form.scoring_format === "a_go_go" && (
              <p className="text-[11px] text-[#1B4D3E] mt-1">Torneo por equipos, sin categorías. Las reglas, equipos y captura se configuran al abrir el torneo.</p>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Campo</Label>
              {(() => {
                const favSet = new Set(favorites);
                const selectedCourse = courses.find((c) => c.id === form.course_id);
                const cq = courseQuery.trim().toLowerCase();
                const filteredCourses = courses
                  .filter((c) => !cq || (c.name || "").toLowerCase().includes(cq) || (c.club_name || "").toLowerCase().includes(cq))
                  .slice()
                  .sort((a, b) => (favSet.has(b.id) ? 1 : 0) - (favSet.has(a.id) ? 1 : 0));
                return (
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setCourseMenuOpen((o) => !o)}
                      data-testid="trn-course-select"
                      className="w-full h-10 px-3 flex items-center justify-between rounded-md border border-input bg-background text-sm"
                    >
                      <span className={selectedCourse ? "" : "text-muted-foreground"}>
                        {selectedCourse ? `${selectedCourse.name} · Par ${selectedCourse.par}` : "Selecciona un campo"}
                      </span>
                      <ChevronDown className="w-4 h-4 opacity-50" />
                    </button>
                    {courseMenuOpen && (
                      <>
                        <div className="fixed inset-0 z-40" onClick={() => setCourseMenuOpen(false)} />
                        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg" data-testid="trn-course-menu">
                          <div className="p-2 border-b sticky top-0 bg-white">
                            <div className="flex items-center gap-2 px-2 h-9 border border-gray-200 rounded-md">
                              <Search className="w-4 h-4 text-[#94A3B8]" />
                              <input
                                autoFocus
                                value={courseQuery}
                                onChange={(e) => setCourseQuery(e.target.value)}
                                placeholder="Buscar campo…"
                                className="flex-1 text-sm outline-none bg-transparent"
                                data-testid="trn-course-search"
                              />
                            </div>
                          </div>
                          <div className="max-h-64 overflow-y-auto py-1">
                            {filteredCourses.length === 0 && <p className="px-3 py-2 text-xs text-[#94A3B8]">Sin resultados.</p>}
                            {filteredCourses.map((c) => {
                              const isFav = favSet.has(c.id);
                              return (
                                <div
                                  key={c.id}
                                  className={`flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer ${form.course_id === c.id ? "bg-[#1B4D3E]/5" : ""}`}
                                  onClick={() => { handleCourseChange(c.id); setCourseMenuOpen(false); setCourseQuery(""); }}
                                  data-testid={`trn-course-option-${c.id}`}
                                >
                                  <button
                                    type="button"
                                    onClick={(e) => toggleFav(e, c.id)}
                                    className="shrink-0"
                                    title={isFav ? "Quitar de favoritos" : "Marcar favorito"}
                                    data-testid={`trn-course-fav-${c.id}`}
                                  >
                                    <Star className={`w-4 h-4 ${isFav ? "fill-amber-400 text-amber-400" : "text-[#CBD5E1]"}`} />
                                  </button>
                                  <span className="flex-1 text-sm truncate">
                                    {c.name} <span className="text-[#94A3B8]">· Par {c.par}</span>
                                    {c.club_name ? <span className="text-[#94A3B8]"> · {c.club_name}</span> : ""}
                                  </span>
                                </div>
                              );
                            })}
                            <div
                              className="px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm border-t"
                              onClick={() => { handleCourseChange("other"); setCourseMenuOpen(false); setCourseQuery(""); }}
                              data-testid="trn-course-option-other"
                            >
                              Otro (escribir manual)
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                );
              })()}
              {!form.course_id && (
                <Input
                  value={form.course_name}
                  onChange={(e) => setForm({ ...form, course_name: e.target.value })}
                  placeholder="Nombre del campo"
                  className="mt-2"
                  data-testid="trn-course-manual"
                />
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Fecha Inicio</Label>
              <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="trn-start" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Fecha Fin</Label>
              <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="trn-end" />
            </div>
          </div>

          {/* Par per hole */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Par por Hoyo (1-18)</Label>
              <span className="text-xs font-bold text-[#1B4D3E]">Par {parTotal}</span>
            </div>
            <div className="grid grid-cols-9 gap-1.5" data-testid="trn-par-holes">
              {form.par_holes.map((p, i) => (
                <div key={i} className="text-center">
                  <div className="text-xs text-[#94A3B8] mb-0.5">{i + 1}</div>
                  <input
                    type="number" min="3" max="6" value={p}
                    onChange={(e) => updPar(i, e.target.value)}
                    className="w-full text-center font-bold text-sm border border-gray-200 rounded py-1 focus:border-[#1B4D3E] focus:outline-none"
                    data-testid={`trn-par-${i + 1}`}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Categories */}
          {form.scoring_format !== "a_go_go" && categories.length > 0 && (
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Categorías del Torneo</Label>
              <p className="text-xs text-[#94A3B8]">Cada categoría se configura independiente: rondas, días, formato y modalidad.</p>
              <div className="flex flex-wrap gap-2 mb-2">
                {categories.map((c) => (
                  <button key={c.category_id} type="button" onClick={() => toggleCat(c.category_id)}
                    className={`px-3 py-1.5 rounded-full border text-sm font-medium transition-colors ${form.category_ids.includes(c.category_id) ? "bg-[#1B4D3E] text-white border-[#1B4D3E]" : "bg-white text-[#64748B] border-gray-200"}`}
                    data-testid={`trn-cat-${c.category_id}`}>
                    {c.name}
                  </button>
                ))}
              </div>
              <div className="space-y-2" data-testid="trn-cat-configs">
                {form.category_ids.map((cid) => {
                  const cat = categories.find((c) => c.category_id === cid);
                  if (!cat) return null;
                  const cfg = form.category_configs[cid] || { rounds: 1, mode: "gross", scoring_format: "medal_play", hcp_percent: 100, playing_days: [] };
                  const selectedCourse = courses.find((c) => c.id === form.course_id);
                  const teeOptions = selectedCourse?.tees ? Object.entries(selectedCourse.tees) : [];
                  return (
                    <details key={cid} className="rounded-lg border border-gray-200 open:border-[#1B4D3E]/40" open>
                      <summary className="cursor-pointer px-4 py-2.5 flex items-center justify-between bg-gray-50 rounded-t-lg">
                        <span className="text-sm font-bold text-[#0F172A]">{cat.name}</span>
                        <span className="text-[11px] text-[#64748B]">
                          {cfg.rounds} R · {cfg.scoring_format === "stableford" ? "Stableford" : cfg.scoring_format === "match_play" ? "Match Play" : "Medal"} · {cfg.mode.toUpperCase()}{cfg.mode === "net" ? ` ${cfg.hcp_percent}%` : ""}
                          {cfg.cut?.enabled ? ` · Corte ${cfg.cut.players} tras R${cfg.cut.after_round}` : ""}
                        </span>
                      </summary>
                      <div className="p-4 space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs">Rondas</Label>
                            <select
                              value={cfg.rounds}
                              onChange={(e) => updateCatCfg(cid, { rounds: parseInt(e.target.value), playing_days: (cfg.playing_days || []).slice(0, parseInt(e.target.value)) })}
                              className="mt-1 h-9 w-full rounded-md border border-gray-200 bg-white px-2 text-sm focus:outline-none focus:border-[#1B4D3E]"
                              data-testid={`trn-cat-rounds-${cid}`}
                            >
                              {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n} ronda{n > 1 ? "s" : ""}</option>)}
                            </select>
                          </div>
                          <div>
                            <Label className="text-xs">Modalidad</Label>
                            <div className="flex gap-1 mt-1">
                              {["gross", "net"].map((m) => (
                                <button
                                  key={m}
                                  type="button"
                                  onClick={() => updateCatCfg(cid, { mode: m })}
                                  className={`flex-1 h-9 rounded-md border text-xs font-semibold ${cfg.mode === m ? "border-[#1B4D3E] bg-[#1B4D3E]/10 text-[#1B4D3E]" : "border-gray-200 text-[#64748B]"}`}
                                  data-testid={`trn-cat-mode-${cid}-${m}`}
                                >
                                  {m.toUpperCase()}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>

                        {cfg.mode === "net" && (
                          <div>
                            <Label className="text-xs">HCP %</Label>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {HCP_PRESETS.map((p) => (
                                <button
                                  key={p}
                                  type="button"
                                  onClick={() => updateCatCfg(cid, { hcp_percent: p })}
                                  className={`px-2 py-1 rounded border text-xs font-semibold ${cfg.hcp_percent === p ? "border-[#1B4D3E] bg-[#1B4D3E]/10 text-[#1B4D3E]" : "border-gray-200 text-[#64748B]"}`}
                                  data-testid={`trn-cat-hcp-${cid}-${p}`}
                                >
                                  {p}%
                                </button>
                              ))}
                              <Input
                                type="number" min="1" max="100"
                                value={cfg.hcp_percent}
                                onChange={(e) => updateCatCfg(cid, { hcp_percent: parseInt(e.target.value) || 100 })}
                                className="w-20 h-8 text-xs"
                              />
                            </div>
                          </div>
                        )}

                        {cfg.mode === "net" && (
                          <div>
                            <Label className="text-xs">Bola (tee) · define rating/slope para el course handicap</Label>
                            {form.course_id ? (
                              teeOptions.length > 0 ? (
                                <div className="flex flex-wrap gap-1 mt-1" data-testid={`trn-cat-tees-${cid}`}>
                                  {teeOptions.map(([key, tee]) => (
                                    <button
                                      key={key}
                                      type="button"
                                      onClick={() => updateCatCfg(cid, { tee: key })}
                                      className={`px-2 py-1 rounded border text-xs font-semibold ${cfg.tee === key ? "border-[#1B4D3E] bg-[#1B4D3E]/10 text-[#1B4D3E]" : "border-gray-200 text-[#64748B]"}`}
                                      data-testid={`trn-cat-tee-${cid}-${key}`}
                                    >
                                      {(tee.name || key)}{tee.slope_rating ? ` · ${tee.course_rating}/${tee.slope_rating}` : ""}
                                    </button>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-[11px] text-amber-600 mt-1">El campo seleccionado no tiene bolas configuradas.</p>
                              )
                            ) : (
                              <p className="text-[11px] text-amber-600 mt-1">Selecciona un campo (arriba) para elegir la bola.</p>
                            )}
                            {form.course_id && teeOptions.length > 0 && !cfg.tee && (
                              <p className="text-[11px] text-red-600 mt-1">Requerido: elige la bola para calcular el hándicap.</p>
                            )}
                          </div>
                        )}

                        <div>
                          <Label className="text-xs">Formato de Juego</Label>
                          <div className="grid grid-cols-3 gap-1 mt-1">
                            {[
                              { k: "medal_play", l: "Medal" },
                              { k: "stableford", l: "Stableford" },
                              { k: "match_play", l: "Match", disabled: true },
                            ].map((f) => (
                              <button
                                key={f.k}
                                type="button"
                                onClick={() => !f.disabled && updateCatCfg(cid, { scoring_format: f.k })}
                                disabled={f.disabled}
                                className={`h-9 rounded-md border text-xs font-semibold relative ${
                                  cfg.scoring_format === f.k
                                    ? "border-[#1B4D3E] bg-[#1B4D3E]/10 text-[#1B4D3E]"
                                    : f.disabled
                                      ? "border-gray-100 text-[#94A3B8] bg-gray-50 cursor-not-allowed"
                                      : "border-gray-200 text-[#64748B]"
                                }`}
                                data-testid={`trn-cat-format-${cid}-${f.k}`}
                              >
                                {f.l}
                                {f.disabled && <span className="absolute -top-1.5 -right-1 text-[8px] bg-amber-100 text-amber-700 px-1 rounded-sm">SOON</span>}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div>
                          <Label className="text-xs">Días de Juego</Label>
                          <div className="grid grid-cols-2 gap-2 mt-1">
                            {Array.from({ length: cfg.rounds }).map((_, i) => (
                              <div key={i}>
                                <p className="text-[10px] text-[#94A3B8] mb-0.5">Ronda {i + 1}</p>
                                <Input
                                  type="date"
                                  min={form.start_date || undefined}
                                  max={form.end_date || undefined}
                                  value={(cfg.playing_days || [])[i] || ""}
                                  onChange={(e) => updateCatPlayingDay(cid, i, e.target.value)}
                                  className="h-8 text-xs"
                                  data-testid={`trn-cat-day-${cid}-${i}`}
                                />
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Corte (per-category cut). Only makes sense with 2+ rounds. */}
                        {cfg.rounds >= 2 && (
                          <div className="pt-2 border-t border-gray-100">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={!!cfg.cut?.enabled}
                                onChange={(e) =>
                                  updateCatCfg(cid, {
                                    cut: e.target.checked
                                      ? { enabled: true, players: cfg.cut?.players || 16, after_round: cfg.cut?.after_round || 1 }
                                      : null,
                                  })
                                }
                                className="w-4 h-4"
                                data-testid={`trn-cat-cut-toggle-${cid}`}
                              />
                              <span className="text-xs font-semibold text-[#0F172A]">Corte</span>
                              <span className="text-[10px] text-[#94A3B8]">(sólo los top-N continúan tras la ronda X)</span>
                            </label>
                            {cfg.cut?.enabled && (
                              <div className="grid grid-cols-2 gap-2 mt-2">
                                <div>
                                  <p className="text-[10px] text-[#94A3B8] mb-0.5">Jugadores que pasan</p>
                                  <Input
                                    type="number"
                                    min={1}
                                    max={999}
                                    value={cfg.cut.players ?? 16}
                                    onChange={(e) =>
                                      updateCatCfg(cid, {
                                        cut: { ...cfg.cut, players: Math.max(1, Math.min(999, parseInt(e.target.value) || 1)) },
                                      })
                                    }
                                    className="h-8 text-xs"
                                    data-testid={`trn-cat-cut-players-${cid}`}
                                  />
                                </div>
                                <div>
                                  <p className="text-[10px] text-[#94A3B8] mb-0.5">Después de la ronda</p>
                                  <Select
                                    value={String(cfg.cut.after_round ?? 1)}
                                    onValueChange={(v) =>
                                      updateCatCfg(cid, { cut: { ...cfg.cut, after_round: parseInt(v) || 1 } })
                                    }
                                  >
                                    <SelectTrigger className="h-8 text-xs" data-testid={`trn-cat-cut-after-${cid}`}>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {Array.from({ length: cfg.rounds - 1 }).map((_, i) => (
                                        <SelectItem key={i + 1} value={String(i + 1)}>Ronda {i + 1}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </details>
                  );
                })}
              </div>

              {/* ─── Método de categorías (Wizard step) ─── */}
              {form.category_ids.length > 0 && (
              <div className="mt-5 pt-5 border-t border-gray-200" data-testid="trn-assignment-wizard">
                <Label className="text-sm font-bold text-[#0F172A]">
                  Método de asignación de jugadores
                </Label>
                <p className="text-xs text-[#94A3B8] mt-1">
                  Elige cómo se asignará cada jugador a su categoría cuando se inscriba al torneo.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
                  {[
                    { k: "range", t: "Por rango de HCP", d: "Cada categoría tiene un rango de Index definido manualmente." },
                    { k: "sequential", t: "Por orden de HCP", d: "Ordena a los jugadores por Index y divide en bloques por categoría." },
                    { k: "mixed", t: "Mixto", d: "Combina categorías por rango con categorías por bloques." },
                  ].map((m) => {
                    const active = (form.category_assignment?.mode || "range") === m.k;
                    return (
                      <button
                        key={m.k}
                        type="button"
                        onClick={() => setAssignMode(m.k)}
                        className={`text-left rounded-xl border-2 p-4 transition-colors ${
                          active
                            ? "border-[#1B4D3E] bg-[#1B4D3E]/5"
                            : "border-gray-200 bg-white hover:border-gray-300"
                        }`}
                        data-testid={`trn-assign-${m.k}`}
                      >
                        <div className="flex items-center gap-2 mb-1.5">
                          <div
                            className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${
                              active ? "border-[#1B4D3E] bg-[#1B4D3E]" : "border-gray-300"
                            }`}
                          />
                          <span className="text-sm font-bold text-[#0F172A]">{m.t}</span>
                        </div>
                        <p className="text-[11px] text-[#64748B] leading-snug">{m.d}</p>
                      </button>
                    );
                  })}
                </div>

                {(form.category_assignment?.mode || "range") !== "range" && (
                  <div className="mt-4 space-y-2" data-testid="trn-assign-rules">
                    <p className="text-[11px] font-semibold text-[#64748B] uppercase tracking-wide">
                      Regla por categoría
                    </p>
                    {form.category_ids.map((cid) => {
                      const cat = categories.find((c) => c.category_id === cid);
                      if (!cat) return null;
                      const rule = form.category_assignment?.rules?.[cid] || { type: "RANGE" };
                      const isMixed = form.category_assignment?.mode === "mixed";
                      return (
                        <div
                          key={`rule-${cid}`}
                          className="flex items-center gap-3 px-3 py-2 rounded-lg bg-gray-50 border border-gray-100"
                          data-testid={`trn-assign-rule-${cid}`}
                        >
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-[#0F172A] truncate">{cat.name}</p>
                            {rule.type === "RANGE" ? (
                              <p className="text-[11px] text-[#64748B]">
                                Rango del maestro: {cat.min_index ?? "≤"} — {cat.max_index ?? "sin tope"}
                              </p>
                            ) : (
                              <p className="text-[11px] text-[#64748B]">Distribución por bloques (Index ↑)</p>
                            )}
                          </div>
                          {isMixed && (
                            <div className="flex gap-1">
                              {[
                                { k: "RANGE", l: "Rango" },
                                { k: "SEQUENTIAL_BLOCK", l: "Bloque" },
                              ].map((t) => (
                                <button
                                  key={t.k}
                                  type="button"
                                  onClick={() =>
                                    setCatRule(cid, t.k === "SEQUENTIAL_BLOCK"
                                      ? { type: "SEQUENTIAL_BLOCK", block_size: rule.block_size || 16 }
                                      : { type: "RANGE" })
                                  }
                                  className={`px-2.5 h-8 rounded-md text-[11px] font-semibold border ${
                                    rule.type === t.k
                                      ? "border-[#1B4D3E] bg-[#1B4D3E]/10 text-[#1B4D3E]"
                                      : "border-gray-200 text-[#64748B]"
                                  }`}
                                  data-testid={`trn-assign-rule-type-${cid}-${t.k}`}
                                >
                                  {t.l}
                                </button>
                              ))}
                            </div>
                          )}
                          {rule.type === "SEQUENTIAL_BLOCK" && (
                            <div className="flex items-center gap-1.5">
                              <span className="text-[10px] text-[#94A3B8]">Jugadores:</span>
                              <Input
                                type="number"
                                min={1}
                                max={200}
                                value={rule.block_size || 16}
                                onChange={(e) =>
                                  setCatRule(cid, { block_size: Math.max(1, Math.min(200, parseInt(e.target.value) || 16)) })
                                }
                                className="w-16 h-8 text-xs text-center"
                                data-testid={`trn-assign-block-${cid}`}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                    <p className="text-[11px] text-[#94A3B8] italic mt-2">
                      Los jugadores sobrantes quedarán "Sin asignar" y podrás moverlos manualmente después de inscribirlos.
                    </p>
                  </div>
                )}
              </div>
              )}
            </div>
          )}

          {/* ─── Tabla de Puntos Stableford (global, sólo si alguna categoría usa Stableford) ─── */}
          {form.category_ids.some((cid) => form.category_configs[cid]?.scoring_format === "stableford") && (
            <div className="space-y-2" data-testid="trn-stableford-table">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-bold text-[#0F172A]">Tabla de Puntos Stableford</Label>
                <button
                  type="button"
                  onClick={resetStablefordTable}
                  className="text-[11px] font-semibold text-[#1B4D3E] hover:underline"
                  data-testid="trn-stableford-reset"
                >
                  Restaurar por defecto
                </button>
              </div>
              <p className="text-xs text-[#94A3B8] -mt-1">
                Puntos que vale cada hoyo según el resultado (neto) respecto al par. Se usa para calcular el leaderboard en automático.
              </p>
              <div className="rounded-xl border border-gray-200 divide-y divide-gray-100 overflow-hidden">
                {form.stableford_table.map((row) => (
                  <div key={row.key} className="flex items-center justify-between px-3 py-2 bg-white">
                    <span className="text-sm text-[#0F172A]">{row.label}</span>
                    <div className="flex items-center gap-1.5">
                      <Input
                        type="number"
                        min={0}
                        max={20}
                        value={row.points}
                        onChange={(e) => setStablefordPoints(row.key, e.target.value)}
                        className="w-16 h-8 text-xs text-center"
                        data-testid={`trn-stableford-pts-${row.key}`}
                      />
                      <span className="text-[11px] text-[#94A3B8] w-9">pts</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-2" data-testid="trn-tiebreaker">
            <Label className="text-sm font-bold text-[#0F172A]">Criterio de Desempate</Label>
            <p className="text-xs text-[#94A3B8] -mt-1">
              Cuando dos jugadores empatan, se desempata por el mejor score en estos tramos de hoyos (de la última ronda jugada), en orden. Estándar USGA por defecto.
            </p>
            <div className="rounded-xl border border-gray-200 divide-y divide-gray-100 overflow-hidden">
              {form.tiebreaker.map((key, idx) => (
                <div key={key} className="flex items-center justify-between px-3 py-2 bg-white" data-testid={`trn-tb-item-${key}`}>
                  <span className="text-sm text-[#0F172A]"><span className="text-[#94A3B8] mr-2">{idx + 1}.</span>{TIEBREAK_LABEL[key]}</span>
                  <div className="flex items-center gap-1">
                    <button type="button" disabled={idx === 0}
                      onClick={() => { const a = form.tiebreaker.slice(); [a[idx - 1], a[idx]] = [a[idx], a[idx - 1]]; setForm({ ...form, tiebreaker: a }); }}
                      className="w-7 h-7 rounded-md border border-gray-200 text-[#64748B] disabled:opacity-30 hover:bg-gray-50" data-testid={`trn-tb-up-${key}`}>↑</button>
                    <button type="button" disabled={idx === form.tiebreaker.length - 1}
                      onClick={() => { const a = form.tiebreaker.slice(); [a[idx + 1], a[idx]] = [a[idx], a[idx + 1]]; setForm({ ...form, tiebreaker: a }); }}
                      className="w-7 h-7 rounded-md border border-gray-200 text-[#64748B] disabled:opacity-30 hover:bg-gray-50" data-testid={`trn-tb-down-${key}`}>↓</button>
                    <button type="button" disabled={form.tiebreaker.length <= 1}
                      onClick={() => setForm({ ...form, tiebreaker: form.tiebreaker.filter((k) => k !== key) })}
                      className="w-7 h-7 rounded-md border border-gray-200 text-red-500 disabled:opacity-30 hover:bg-red-50" data-testid={`trn-tb-remove-${key}`}>×</button>
                  </div>
                </div>
              ))}
            </div>
            {TIEBREAK_OPTIONS.some((o) => !form.tiebreaker.includes(o.key)) && (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {TIEBREAK_OPTIONS.filter((o) => !form.tiebreaker.includes(o.key)).map((o) => (
                  <button key={o.key} type="button"
                    onClick={() => setForm({ ...form, tiebreaker: [...form.tiebreaker, o.key] })}
                    className="px-3 h-8 rounded-full text-xs font-semibold border border-gray-200 bg-white text-[#64748B] hover:border-[#1B4D3E] hover:text-[#1B4D3E]"
                    data-testid={`trn-tb-add-${o.key}`}>+ {o.label}</button>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Notas</Label>
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Opcional" data-testid="trn-notes" />
          </div>
        </div>
        <div className="sticky bottom-0 bg-white border-t border-gray-100 px-6 py-4 flex gap-3">
          <Button variant="outline" className="flex-1" onClick={onClose}>Cancelar</Button>
          <Button className="flex-1 bg-[#1B4D3E] hover:bg-[#163F33] text-white" onClick={handleSave} disabled={saving} data-testid="trn-save-btn">
            {saving ? "Guardando..." : tournament ? "Guardar" : "Crear Torneo"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ==========================================================================
// Tournament Detail (Tabs: Info | Players | TeeTimes | Capture | Leaderboard)
// ==========================================================================

function TournamentDetail({ tournamentId, canWrite, onBack }) {
  const [tab, setTab] = useState("convocatoria");
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  const fetchT = useCallback(async () => {
    try {
      const res = await fetch(`${API}/club/tournaments/${tournamentId}`, { credentials: "include", headers: AUTH() });
      if (res.ok) setTournament(await res.json());
    } finally { setLoading(false); }
  }, [tournamentId]);

  useEffect(() => { fetchT(); }, [fetchT]);

  const handleDelete = () => setShowDelete(true);

  const confirmDelete = async () => {
    const res = await fetch(`${API}/club/tournaments/${tournamentId}`, { method: "DELETE", credentials: "include", headers: AUTH() });
    if (!res.ok) { toast.error("Error al eliminar"); return; }
    toast.success("Torneo eliminado");
    onBack();
  };

  const handleFinalize = async () => {
    if (!window.confirm("Finalizar torneo? Los resultados quedaran congelados.")) return;
    try {
      const res = await fetch(`${API}/club/tournaments/${tournamentId}/finalize`, { method: "POST", credentials: "include", headers: AUTH() });
      if (!res.ok) throw new Error();
      toast.success("Torneo finalizado");
      fetchT();
    } catch { toast.error("Error al finalizar"); }
  };

  const handleTogglePublic = async () => {
    try {
      const res = await fetch(`${API}/club/tournaments/${tournament.tournament_id}`, {
        method: "PUT", credentials: "include", headers: AUTH(),
        body: JSON.stringify({ is_public: !tournament.is_public }),
      });
      if (!res.ok) throw new Error();
      toast.success(!tournament.is_public ? "Torneo publicado: visible para todos los usuarios de cadii" : "Torneo oculto del feed público");
      fetchT();
    } catch { toast.error("No se pudo cambiar la visibilidad"); }
  };

  if (loading) return <div className="p-8 flex justify-center"><div className="w-8 h-8 border-4 border-[#1B4D3E] border-t-transparent rounded-full animate-spin" /></div>;
  if (!tournament) return null;

  const st = STATUS_LABEL[tournament.status] || STATUS_LABEL.draft;
  const isCompleted = tournament.status === "completed";
  const isAgogo = tournament.scoring_format === "a_go_go";

  const tabs = [
    { k: "convocatoria", l: "Convocatoria", icon: FileText },
    { k: "field", l: "Field", icon: Users },
    { k: "info", l: "Info", icon: Trophy },
    { k: "players", l: "Jugadores", icon: Users },
    { k: "teetimes", l: "Tee Times", icon: Clock },
    { k: "capture", l: "Captura", icon: ClipboardList },
    { k: "leaderboard", l: "Leaderboard", icon: BarChart3 },
    { k: "putting", l: "Putts", icon: Flag },
    { k: "events", l: "Eventos", icon: Calendar },
    { k: "gallery", l: "Galeria", icon: ImageIcon },
  ];

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto">
      <button onClick={onBack} className="flex items-center gap-2 text-sm text-[#64748B] hover:text-[#0F172A] mb-4" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" /> Volver
      </button>
      <div className="flex items-start justify-between mb-4 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A]">{tournament.name}</h1>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${st.cls}`}>{st.txt}</span>
            <span className="text-xs bg-[#1B4D3E]/10 text-[#1B4D3E] px-2 py-0.5 rounded font-medium">{tournament.mode === "net" ? `NET ${tournament.hcp_percent}%` : "GROSS"}</span>
            <span className="text-xs text-[#64748B]">{tournament.rounds} ronda{tournament.rounds > 1 ? "s" : ""} · Par {tournament.par_total}</span>
            {tournament.is_public && <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-semibold" data-testid="public-badge">● Público</span>}
          </div>
        </div>
        {canWrite && (
          <div className="flex gap-2">
            {!isCompleted && (
              <>
                <Button variant="outline" size="sm" onClick={() => setShowEdit(true)} data-testid="edit-tournament-btn"><Pencil className="w-3.5 h-3.5 mr-1" /> Editar</Button>
                <Button size="sm" onClick={handleFinalize} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="finalize-btn"><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Finalizar</Button>
              </>
            )}
            <Button variant="outline" size="sm" onClick={handleTogglePublic} data-testid="toggle-public-btn" className={tournament.is_public ? "border-emerald-300 text-emerald-700" : ""}>
              <Globe className="w-3.5 h-3.5 mr-1" /> {tournament.is_public ? "Quitar público" : "Hacer público"}
            </Button>
            <Button variant="outline" size="sm" onClick={handleDelete} className="text-red-600 border-red-200" data-testid="delete-tournament-btn"><Trash2 className="w-3.5 h-3.5" /></Button>
          </div>
        )}
      </div>

      {showEdit && (
        <TournamentForm tournament={tournament} onClose={() => setShowEdit(false)} onSaved={() => { setShowEdit(false); fetchT(); }} />
      )}

      {/* Tabs */}
      {isAgogo ? (
        <AGoGoManager tournament={tournament} canWrite={canWrite && !isCompleted} onChange={fetchT} />
      ) : (
      <>
      <div className="flex gap-1 mb-6 overflow-x-auto" data-testid="trn-tabs">
        {tabs.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.k} onClick={() => setTab(t.k)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 whitespace-nowrap ${tab === t.k ? "bg-[#1B4D3E] text-white" : "text-[#64748B] hover:bg-gray-100"}`}
              data-testid={`tab-${t.k}`}>
              <Icon className="w-4 h-4" /> {t.l}
            </button>
          );
        })}
      </div>

      {tab === "convocatoria" && <ConvocatoriaTab key={tournament.updated_at || "conv"} tournament={tournament} canWrite={canWrite && !isCompleted} onChange={fetchT} />}
      {tab === "field" && <FieldTab tournament={tournament} />}
      {tab === "info" && <InfoTab tournament={tournament} />}
      {tab === "players" && <PlayersTab tournament={tournament} canWrite={canWrite && !isCompleted} onChange={fetchT} />}
      {tab === "teetimes" && <TeeTimesTab tournament={tournament} canWrite={canWrite && !isCompleted} />}
      {tab === "capture" && <MarshallCapture tournament={tournament} canWrite={canWrite && !isCompleted} />}
      {tab === "leaderboard" && <LeaderboardTab tournament={tournament} />}
      {tab === "putting" && <PuttingTab tournament={tournament} canWrite={canWrite} />}
      {tab === "events" && <EventsTab tournament={tournament} canWrite={canWrite && !isCompleted} />}
      {tab === "gallery" && <GalleryTab tournament={tournament} canWrite={canWrite && !isCompleted} />}

      </>
      )}

      <ConfirmDeleteModal
        open={showDelete}
        onOpenChange={setShowDelete}
        entityName={tournament?.name}
        entityLabel="torneo"
        description="Esta acción es permanente y no se puede deshacer."
        cascade={["Jugadores inscritos", "Tee times generados", "Scores capturados", "Eventos y galería"]}
        onConfirm={confirmDelete}
        testId="delete-tournament-modal"
      />
    </div>
  );
}

// ==========================================================================
// Tabs
// ==========================================================================

// "2026-06-17" → "Vie 17 Jun" (Spanish, timezone-safe).
function fmtPlayingDay(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso + "T12:00:00");
    return d.toLocaleDateString("es-MX", { weekday: "short", day: "numeric", month: "short" });
  } catch { return iso; }
}

function ConvocatoriaTab({ tournament, canWrite, onChange }) {
  const tid = tournament.tournament_id;
  const cats = tournament.categories || [];

  // Read both new (image_*) and legacy (pdf_*) fields so previously saved
  // tournaments still render their convocatoria.
  const initialImgUrl = tournament.convocatoria_image_url || tournament.convocatoria_pdf_url || "";
  const initialImgName = tournament.convocatoria_image_name || tournament.convocatoria_pdf_name || "";
  const [imgUrl, setImgUrl] = useState(initialImgUrl);
  const [imgName, setImgName] = useState(initialImgName);
  const [rulesText, setRulesText] = useState(tournament.local_rules_text || "");
  const [rulesPdfUrl, setRulesPdfUrl] = useState(tournament.local_rules_pdf_url || "");
  const [rulesPdfName, setRulesPdfName] = useState(tournament.local_rules_pdf_name || "");
  const [contacts, setContacts] = useState(tournament.contacts || []);
  // Round schedules per category: cid -> [{round_num, date, slot_ids: []}, ...]
  // Auto-migrate from playing_days when round_schedules is empty.
  const [roundSchedules, setRoundSchedules] = useState(() => {
    const map = {};
    for (const c of cats) {
      const existing = Array.isArray(c.round_schedules) ? c.round_schedules : [];
      const rows = [];
      for (let r = 1; r <= (c.rounds || 1); r++) {
        const fromExisting = existing.find((x) => x.round_num === r);
        const legacyDate = (c.playing_days || [])[r - 1] || "";
        rows.push({
          round_num: r,
          date: fromExisting?.date || legacyDate || "",
          slot_ids: fromExisting?.slot_ids ? [...fromExisting.slot_ids] : [],
        });
      }
      map[c.category_id] = rows;
    }
    return map;
  });
  // Available club tee slots — used to populate the per-round checkbox list.
  const [slots, setSlots] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadingRules, setUploadingRules] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSlots();
  }, []);

  const loadSlots = () => {
    fetch(`${API}/club/tee-slots`, { credentials: "include", headers: AUTH() })
      .then((r) => r.ok ? r.json() : [])
      .then((data) => setSlots(Array.isArray(data) ? data : []))
      .catch(() => setSlots([]));
  };

  const uploadImage = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Solo imágenes (JPG/PNG/WEBP)"); return; }
    if (file.size > 15 * 1024 * 1024) { toast.error("Máximo 15MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/club/tournaments/${tid}/uploads`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("cadii_club_token") || ""}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Error");
      setImgUrl(data.url); setImgName(data.name);
      toast.success("Imagen subida");
    } catch (e) {
      toast.error(e.message || "Error subiendo imagen");
    } finally { setUploading(false); }
  };

  const uploadRulesPdf = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf") { toast.error("Solo PDF"); return; }
    if (file.size > 15 * 1024 * 1024) { toast.error("Máximo 15MB"); return; }
    setUploadingRules(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/club/tournaments/${tid}/uploads`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("cadii_club_token") || ""}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Error");
      setRulesPdfUrl(data.url); setRulesPdfName(data.name);
      toast.success("PDF subido");
    } catch (e) {
      toast.error(e.message || "Error subiendo PDF");
    } finally { setUploadingRules(false); }
  };

  // Round schedule mutators
  const setRoundDate = (cid, roundNum, date) => {
    setRoundSchedules((m) => ({
      ...m,
      [cid]: (m[cid] || []).map((r) => r.round_num === roundNum ? { ...r, date } : r),
    }));
  };
  const toggleRoundSlot = (cid, roundNum, slotId) => {
    setRoundSchedules((m) => ({
      ...m,
      [cid]: (m[cid] || []).map((r) => {
        if (r.round_num !== roundNum) return r;
        const has = r.slot_ids.includes(slotId);
        return { ...r, slot_ids: has ? r.slot_ids.filter((s) => s !== slotId) : [...r.slot_ids, slotId] };
      }),
    }));
  };

  const addContact = () => setContacts((cs) => [...cs, { name: "", role: "", phone: "", email: "" }]);
  const removeContact = (i) => setContacts((cs) => cs.filter((_, idx) => idx !== i));
  const updateContact = (i, k, v) => setContacts((cs) => cs.map((c, idx) => (idx === i ? { ...c, [k]: v } : c)));

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/club/tournaments/${tid}/convocatoria`, {
        method: "PUT", credentials: "include", headers: AUTH(),
        body: JSON.stringify({
          convocatoria_image_url: imgUrl, convocatoria_image_name: imgName,
          local_rules_text: rulesText,
          local_rules_pdf_url: rulesPdfUrl, local_rules_pdf_name: rulesPdfName,
          contacts,
          round_schedules: roundSchedules,
        }),
      });
      if (!res.ok) throw new Error();
      toast.success("Convocatoria guardada");
      onChange?.();
    } catch { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="convocatoria-tab">
      {/* Imagen de la convocatoria */}
      <Card className="card-default">
        <CardContent className="p-5 space-y-3">
          <h3 className="text-sm font-bold text-[#0F172A] flex items-center gap-2">
            <ImageIcon className="w-4 h-4 text-[#1B4D3E]" /> Imagen de la convocatoria
          </h3>
          {imgUrl ? (
            <div className="space-y-2">
              <img src={imgUrl} alt={imgName || "Convocatoria"} className="w-full max-h-96 object-contain rounded-lg border border-gray-200 bg-gray-50" data-testid="convocatoria-image" />
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#64748B] truncate flex-1">{imgName || "Convocatoria"}</span>
                {canWrite && (
                  <button onClick={() => { setImgUrl(""); setImgName(""); }} className="text-red-500 flex items-center gap-1 text-xs font-medium" data-testid="clear-convocatoria-image">
                    <X className="w-3.5 h-3.5" /> Reemplazar
                  </button>
                )}
              </div>
            </div>
          ) : canWrite ? (
            <label className="flex items-center gap-2 p-4 border-2 border-dashed border-gray-200 rounded-lg cursor-pointer hover:border-[#1B4D3E] transition-colors">
              <Upload className="w-4 h-4 text-[#64748B]" />
              <span className="text-sm text-[#64748B]">
                {uploading ? "Subiendo..." : "Subir imagen JPG/PNG (hasta 15MB) — se mostrará dentro de la app sin abrir archivos"}
              </span>
              <input
                type="file" className="hidden" accept="image/*"
                onChange={(e) => uploadImage(e.target.files?.[0])}
                disabled={uploading}
                data-testid="convocatoria-image-input"
              />
            </label>
          ) : <p className="text-sm text-[#94A3B8]">Sin imagen adjunta</p>}
        </CardContent>
      </Card>

      {/* Calendario / programación por ronda */}
      <Card className="card-default">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="text-sm font-bold text-[#0F172A] flex items-center gap-2">
              <Calendar className="w-4 h-4 text-[#1B4D3E]" /> Calendario de juego
            </h3>
            {canWrite && cats.length > 0 && (
              <CalendarCsvImport tid={tid} categories={cats} onImported={() => { loadSlots(); onChange?.(); }} />
            )}
          </div>
          {cats.length === 0 ? (
            <p className="text-sm text-[#94A3B8]">Este torneo aún no tiene categorías. Edita el torneo para agregarlas.</p>
          ) : cats.map((c) => (
            <div key={c.category_id} className="border border-gray-100 rounded-lg p-3" data-testid={`schedule-cat-${c.category_id}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="font-semibold text-[#0F172A] text-sm">{c.name}</span>
                <span className="text-xs text-[#64748B]">{c.rounds} ronda{c.rounds > 1 ? "s" : ""}</span>
              </div>
              <div className="space-y-3">
                {(roundSchedules[c.category_id] || []).map((r) => (
                  <RoundScheduleRow
                    key={r.round_num}
                    catId={c.category_id}
                    row={r}
                    slots={slots}
                    canWrite={canWrite}
                    onDateChange={(date) => setRoundDate(c.category_id, r.round_num, date)}
                    onToggleSlot={(slotId) => toggleRoundSlot(c.category_id, r.round_num, slotId)}
                  />
                ))}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Reglas locales */}
      <Card className="card-default">
        <CardContent className="p-5 space-y-3">
          <h3 className="text-sm font-bold text-[#0F172A] flex items-center gap-2">
            <Info className="w-4 h-4 text-[#1B4D3E]" /> Reglas locales
          </h3>
          <textarea
            value={rulesText}
            onChange={(e) => setRulesText(e.target.value)}
            disabled={!canWrite}
            className="w-full min-h-[140px] rounded-lg border border-gray-200 p-3 text-sm focus:outline-none focus:border-[#1B4D3E]"
            placeholder="Describe reglas locales (una por línea)…"
            data-testid="local-rules-text"
          />
          {rulesPdfUrl ? (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-[#1B4D3E]/5 border border-[#1B4D3E]/10">
              <FileText className="w-4 h-4 text-[#1B4D3E]" />
              <a href={rulesPdfUrl} target="_blank" rel="noreferrer" className="text-sm text-[#1B4D3E] font-medium flex-1 truncate hover:underline" data-testid="rules-pdf-link">
                {rulesPdfName || "Reglas.pdf"}
              </a>
              {canWrite && (
                <button onClick={() => { setRulesPdfUrl(""); setRulesPdfName(""); }} className="text-red-500" data-testid="clear-rules-pdf">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          ) : canWrite ? (
            <label className="flex items-center gap-2 p-3 border-2 border-dashed border-gray-200 rounded-lg cursor-pointer hover:border-[#1B4D3E] transition-colors">
              <Upload className="w-4 h-4 text-[#64748B]" />
              <span className="text-sm text-[#64748B]">{uploadingRules ? "Subiendo..." : "PDF de reglas (opcional)"}</span>
              <input
                type="file" className="hidden" accept="application/pdf"
                onChange={(e) => uploadRulesPdf(e.target.files?.[0])}
                disabled={uploadingRules}
                data-testid="rules-pdf-input"
              />
            </label>
          ) : null}
        </CardContent>
      </Card>

      {/* Contactos */}
      <Card className="card-default">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-[#0F172A] flex items-center gap-2">
              <Users className="w-4 h-4 text-[#1B4D3E]" /> Contactos del torneo
            </h3>
            {canWrite && (
              <Button size="sm" variant="outline" onClick={addContact} data-testid="add-contact-btn">
                <Plus className="w-3.5 h-3.5 mr-1" /> Agregar
              </Button>
            )}
          </div>
          {contacts.length === 0 ? (
            <p className="text-sm text-[#94A3B8]">Sin contactos configurados</p>
          ) : contacts.map((c, i) => (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2 border border-gray-100 rounded-lg p-3" data-testid={`contact-${i}`}>
              <Input placeholder="Nombre" value={c.name || ""} onChange={(e) => updateContact(i, "name", e.target.value)} disabled={!canWrite} data-testid={`contact-name-${i}`} />
              <Input placeholder="Rol (Director…)" value={c.role || ""} onChange={(e) => updateContact(i, "role", e.target.value)} disabled={!canWrite} data-testid={`contact-role-${i}`} />
              <Input placeholder="Teléfono" value={c.phone || ""} onChange={(e) => updateContact(i, "phone", e.target.value)} disabled={!canWrite} data-testid={`contact-phone-${i}`} />
              <div className="flex gap-1">
                <Input placeholder="Email" type="email" value={c.email || ""} onChange={(e) => updateContact(i, "email", e.target.value)} disabled={!canWrite} className="flex-1" data-testid={`contact-email-${i}`} />
                {canWrite && (
                  <button onClick={() => removeContact(i)} className="text-red-500 hover:text-red-700 px-2" data-testid={`remove-contact-${i}`} aria-label="Eliminar contacto">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {canWrite && (
        <div className="flex justify-end">
          <Button
            className="bg-[#1B4D3E] hover:bg-[#163F33] text-white"
            onClick={save} disabled={saving}
            data-testid="convocatoria-save-btn"
          >
            {saving ? "Guardando..." : "Guardar convocatoria"}
          </Button>
        </div>
      )}
    </div>
  );
}

// Compact per-round editor: date + expandable checklist of tee slots.
function RoundScheduleRow({ catId, row, slots, canWrite, onDateChange, onToggleSlot }) {
  const [open, setOpen] = useState(false);
  const rangeLabel = useMemo(() => {
    if (row.slot_ids.length === 0) return "Seleccionar horarios";
    const chosen = slots.filter((s) => row.slot_ids.includes(s.slot_id));
    if (chosen.length === 0) return `${row.slot_ids.length} slot${row.slot_ids.length === 1 ? "" : "s"}`;
    const times = chosen.map((s) => s.time).sort();
    return `${times[0]} – ${times[times.length - 1]} · ${chosen.length} slot${chosen.length === 1 ? "" : "s"}`;
  }, [row.slot_ids, slots]);

  return (
    <div className="rounded-md bg-gray-50/60 border border-gray-100" data-testid={`round-row-${catId}-${row.round_num}`}>
      <div className="p-2.5 flex items-center gap-2 flex-wrap">
        <span className="w-16 text-xs font-bold text-[#1B4D3E] uppercase tracking-wide">R{row.round_num}</span>
        <input
          type="date"
          value={row.date || ""}
          onChange={(e) => onDateChange(e.target.value)}
          disabled={!canWrite}
          className="h-8 rounded border border-gray-200 px-2 text-xs"
          data-testid={`round-date-${catId}-${row.round_num}`}
        />
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          disabled={!canWrite || slots.length === 0}
          className="flex-1 min-w-[180px] text-left h-8 rounded border border-gray-200 px-2 text-xs bg-white hover:border-[#1B4D3E] disabled:opacity-50 flex items-center justify-between"
          data-testid={`round-slots-toggle-${catId}-${row.round_num}`}
        >
          <span className="truncate text-[#0F172A]">{slots.length === 0 ? "Sin slots configurados en el club" : rangeLabel}</span>
          <span className="text-[#64748B] ml-2">▾</span>
        </button>
      </div>
      {open && slots.length > 0 && (
        <div className="border-t border-gray-100 p-2 max-h-52 overflow-y-auto grid grid-cols-2 sm:grid-cols-3 gap-1" data-testid={`round-slots-panel-${catId}-${row.round_num}`}>
          {slots.map((s) => {
            const checked = row.slot_ids.includes(s.slot_id);
            return (
              <label
                key={s.slot_id}
                className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded cursor-pointer ${checked ? "bg-[#1B4D3E]/10 text-[#1B4D3E]" : "hover:bg-white"}`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => onToggleSlot(s.slot_id)}
                  className="accent-[#1B4D3E]"
                  data-testid={`round-slot-${catId}-${row.round_num}-${s.slot_id}`}
                />
                <span className="font-semibold">{s.time}</span>
                <span className="text-[#64748B]">H{s.hole ?? 1}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}


function FieldTab({ tournament }) {
  const tid = tournament.tournament_id;
  const cats = tournament.categories || [];
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("all");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API}/club/tournaments/${tid}/players`, { credentials: "include", headers: AUTH() });
        if (res.ok && alive) setPlayers(await res.json());
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [tid]);

  const counts = useMemo(() => {
    const c = { all: players.length, none: 0 };
    for (const p of players) {
      if (!p.category_id) c.none += 1;
      else c[p.category_id] = (c[p.category_id] || 0) + 1;
    }
    return c;
  }, [players]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return players.filter((p) => {
      if (catFilter === "none" && p.category_id) return false;
      if (catFilter !== "all" && catFilter !== "none" && p.category_id !== catFilter) return false;
      if (term && !(p.name || "").toLowerCase().includes(term)) return false;
      return true;
    });
  }, [players, q, catFilter]);

  if (loading) {
    return <div className="flex justify-center py-8"><div className="w-6 h-6 border-3 border-[#1B4D3E] border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-3" data-testid="field-tab">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm text-[#64748B]" data-testid="field-count">
          {filtered.length} de {players.length} competidor{players.length === 1 ? "" : "es"}
        </span>
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar por nombre…"
          className="w-full sm:w-64"
          data-testid="field-search"
        />
      </div>

      {(cats.length > 0 || counts.none > 0) && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="field-filter">
          <button
            onClick={() => setCatFilter("all")}
            className={`px-3 h-8 rounded-full text-xs font-semibold border transition-colors ${
              catFilter === "all" ? "bg-[#1B4D3E] text-white border-[#1B4D3E]" : "bg-white text-[#64748B] border-gray-200"
            }`}
            data-testid="field-cat-all"
          >
            Todos <span className="opacity-70 ml-1">{counts.all}</span>
          </button>
          {cats.map((c) => (
            <button
              key={c.category_id}
              onClick={() => setCatFilter(c.category_id)}
              className={`px-3 h-8 rounded-full text-xs font-semibold border transition-colors ${
                catFilter === c.category_id ? "bg-[#1B4D3E] text-white border-[#1B4D3E]" : "bg-white text-[#64748B] border-gray-200"
              }`}
              data-testid={`field-cat-${c.category_id}`}
            >
              {c.name} <span className="opacity-70 ml-1">{counts[c.category_id] || 0}</span>
            </button>
          ))}
          {counts.none > 0 && (
            <button
              onClick={() => setCatFilter("none")}
              className={`px-3 h-8 rounded-full text-xs font-semibold border transition-colors ${
                catFilter === "none" ? "bg-amber-500 text-white border-amber-500" : "bg-white text-amber-700 border-amber-200"
              }`}
              data-testid="field-cat-none"
            >
              Sin categoría <span className="opacity-70 ml-1">{counts.none}</span>
            </button>
          )}
        </div>
      )}

      {players.length === 0 ? (
        <Card className="card-default"><CardContent className="py-12 text-center text-[#94A3B8]">Sin competidores inscritos aún</CardContent></Card>
      ) : filtered.length === 0 ? (
        <Card className="card-default"><CardContent className="py-12 text-center text-[#94A3B8]">Ningún competidor coincide con los filtros</CardContent></Card>
      ) : (
        <Card className="card-default">
          <CardContent className="p-0">
            <table className="w-full text-sm" data-testid="field-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-2 text-xs font-semibold text-[#64748B]">COMPETIDOR</th>
                  <th className="text-center px-4 py-2 text-xs font-semibold text-[#64748B]">INDEX</th>
                  <th className="text-center px-4 py-2 text-xs font-semibold text-[#1B4D3E]">HCP</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold text-[#64748B]">CATEGORIA</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.player_id} className="border-t border-gray-50 hover:bg-gray-50/50" data-testid={`field-row-${p.player_id}`}>
                    <td className="px-4 py-2 font-medium text-[#0F172A]">{p.name}</td>
                    <td className="text-center px-4 py-2 text-[#64748B]">{p.handicap_index ?? "—"}</td>
                    <td className="text-center px-4 py-2 font-semibold text-[#1B4D3E]" data-testid={`field-hcp-${p.player_id}`} title={p.tee_name ? `Bola ${p.tee_name}` : ""}>{p.playing_handicap != null ? p.playing_handicap : "—"}</td>
                    <td className="px-4 py-2 text-[#64748B]">{p.category_name || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}


function InfoTab({ tournament }) {
  const t = tournament;
  return (
    <Card className="card-default">
      <CardContent className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
        <div><div className="text-xs text-[#94A3B8]">Campo</div><div className="font-medium text-[#0F172A]">{t.course_name || "—"}</div></div>
        <div><div className="text-xs text-[#94A3B8]">Fechas</div><div className="font-medium text-[#0F172A]">{t.start_date || "—"} {t.end_date && `→ ${t.end_date}`}</div></div>
        <div><div className="text-xs text-[#94A3B8]">Modalidad</div><div className="font-medium text-[#0F172A]">Stroke Play · {t.mode === "net" ? `NET ${t.hcp_percent}%` : "GROSS"}</div></div>
        <div><div className="text-xs text-[#94A3B8]">Rondas</div><div className="font-medium text-[#0F172A]">{t.rounds}</div></div>
        <div className="col-span-full">
          <div className="text-xs text-[#94A3B8] mb-1.5">Par por hoyo (Total: {t.par_total})</div>
          <div className="grid grid-cols-9 gap-1 text-center">
            {(t.par_holes || []).map((p, i) => (
              <div key={i} className="border border-gray-100 rounded py-1.5">
                <div className="text-[10px] text-[#94A3B8]">{i + 1}</div>
                <div className="font-bold text-[#0F172A]">{p}</div>
              </div>
            ))}
          </div>
        </div>
        {t.categories?.length > 0 && (
          <div className="col-span-full">
            <div className="text-xs text-[#94A3B8] mb-1">Categorias</div>
            <div className="flex flex-wrap gap-1.5">
              {t.categories.map((c) => <span key={c.category_id} className="text-xs bg-[#1B4D3E]/10 text-[#1B4D3E] px-2 py-0.5 rounded-full">{c.name}</span>)}
            </div>
          </div>
        )}
        {t.notes && <div className="col-span-full"><div className="text-xs text-[#94A3B8]">Notas</div><div className="text-[#64748B]">{t.notes}</div></div>}
      </CardContent>
    </Card>
  );
}

function PlayersTab({ tournament, canWrite, onChange }) {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [preview, setPreview] = useState(null); // { summary, unassigned, warnings, assignment, assignments }
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [movedMap, setMovedMap] = useState({}); // { player_id: category_id | null } — current DnD state
  const [originalMap, setOriginalMap] = useState({}); // snapshot from preview fetch
  const [dragPid, setDragPid] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [savingMoves, setSavingMoves] = useState(false);
  const [catFilter, setCatFilter] = useState("all"); // "all" | category_id | "none"
  const [selected, setSelected] = useState(new Set());
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const tid = tournament.tournament_id;
  const cats = tournament.categories || [];
  const hasAssignment = !!tournament.category_assignment;

  const fetchP = useCallback(async () => {
    try {
      const res = await fetch(`${API}/club/tournaments/${tid}/players`, { credentials: "include", headers: AUTH() });
      if (res.ok) setPlayers(await res.json());
    } finally { setLoading(false); }
  }, [tid]);
  useEffect(() => { fetchP(); }, [fetchP]);

  const remove = async (pid) => {
    if (!window.confirm("Eliminar jugador?")) return;
    try {
      const res = await fetch(`${API}/club/tournaments/${tid}/players/${pid}`, { method: "DELETE", credentials: "include", headers: AUTH() });
      if (!res.ok) throw new Error();
      toast.success("Eliminado");
      fetchP(); onChange?.();
    } catch { toast.error("Error"); }
  };

  const toggleOne = (pid) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(pid)) n.delete(pid); else n.add(pid);
      return n;
    });
  };
  const clearSelection = () => setSelected(new Set());

  const bulkDelete = async () => {
    setBulkDeleting(true);
    try {
      const res = await fetch(`${API}/club/tournaments/${tid}/players/bulk-delete`, {
        method: "POST", credentials: "include", headers: AUTH(),
        body: JSON.stringify({ player_ids: Array.from(selected) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Error");
      const { deleted = 0, skipped = [] } = data;
      if (deleted > 0) toast.success(`${deleted} jugador${deleted === 1 ? "" : "es"} eliminado${deleted === 1 ? "" : "s"}`);
      if (skipped.length > 0) {
        const names = skipped.slice(0, 3).map((s) => s.name).join(", ");
        const more = skipped.length > 3 ? ` +${skipped.length - 3}` : "";
        toast.warning(`${skipped.length} omitido${skipped.length === 1 ? "" : "s"} por tener scores capturados: ${names}${more}`);
      }
      clearSelection();
      fetchP(); onChange?.();
    } catch (e) {
      toast.error(typeof e.message === "string" ? e.message : "Error");
    } finally { setBulkDeleting(false); }
  };

  const moveCategory = async (pid, cid) => {
    try {
      const res = await fetch(`${API}/club/tournaments/${tid}/players/${pid}`, {
        method: "PUT", credentials: "include", headers: AUTH(),
        body: JSON.stringify({ category_id: cid || null }),
      });
      if (!res.ok) throw new Error();
      toast.success("Categoría actualizada");
      fetchP(); onChange?.();
    } catch { toast.error("Error al mover"); }
  };

  const promoteToMember = async (pid) => {
    if (!window.confirm("Agregar este invitado al padrón del club?")) return;
    try {
      const res = await fetch(`${API}/club/tournaments/${tid}/players/${pid}/promote-to-member`, {
        method: "POST", credentials: "include", headers: AUTH(), body: "{}",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Error");
      toast.success(`${data.member.first_name} agregado al padrón`);
      fetchP(); onChange?.();
    } catch (e) { toast.error(typeof e.message === "string" ? e.message : "Error"); }
  };

  const runPreview = async () => {
    setPreviewing(true);
    try {
      const res = await fetch(`${API}/club/tournaments/${tid}/categories/preview`, {
        method: "POST", credentials: "include", headers: AUTH(), body: "{}",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Error");
      setPreview(data);
      setOriginalMap({ ...(data.assignments || {}) });
      setMovedMap({ ...(data.assignments || {}) });
    } catch (e) {
      toast.error(typeof e.message === "string" ? e.message : "Error");
    } finally { setPreviewing(false); }
  };

  const applyAssignment = async () => {
    if (!window.confirm("Aplicar el algoritmo? Esto reemplazará cualquier asignación manual previa.")) return;
    setApplying(true);
    try {
      const res = await fetch(`${API}/club/tournaments/${tid}/categories/apply`, {
        method: "POST", credentials: "include", headers: AUTH(), body: "{}",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Error");
      toast.success(`${data.updated} jugadores actualizados`);
      setPreview(data);
      setOriginalMap({ ...(data.assignments || {}) });
      setMovedMap({ ...(data.assignments || {}) });
      fetchP(); onChange?.();
    } catch (e) {
      toast.error(typeof e.message === "string" ? e.message : "Error");
    } finally { setApplying(false); }
  };

  // Drag & drop handlers. targetCid === null means "Sin asignar".
  const dropOn = (targetCid) => {
    if (!dragPid) return;
    if (movedMap[dragPid] === (targetCid || null)) { setDragPid(null); setDragOver(null); return; }
    setMovedMap((m) => ({ ...m, [dragPid]: targetCid || null }));
    setDragPid(null);
    setDragOver(null);
  };

  // Derived views: recompute summary + unassigned from movedMap so the UI
  // stays in sync while the user drags players around.
  const derivedView = useMemo(() => {
    if (!preview) return null;
    const catsById = new Map(preview.summary.map((s) => [s.category_id, s]));
    const summary = preview.summary.map((s) => ({ ...s, players: [], count: 0, min_resulting: null, max_resulting: null }));
    const summaryIdx = new Map(summary.map((s, i) => [s.category_id, i]));
    const allPlayers = [
      ...preview.summary.flatMap((s) => s.players || []),
      ...(preview.unassigned || []),
    ];
    const playerById = new Map(allPlayers.map((p) => [p.player_id, p]));
    const unassigned = [];
    for (const [pid, cid] of Object.entries(movedMap)) {
      const p = playerById.get(pid);
      if (!p) continue;
      if (!cid || !summaryIdx.has(cid)) { unassigned.push(p); continue; }
      const idx = summaryIdx.get(cid);
      summary[idx].players.push(p);
    }
    // Sort each cat's players by index ASC and recompute min/max.
    for (const s of summary) {
      s.players.sort((a, b) => (a.handicap_index ?? 999) - (b.handicap_index ?? 999));
      s.count = s.players.length;
      const idxs = s.players.map((p) => p.handicap_index).filter((v) => typeof v === "number");
      s.min_resulting = idxs.length ? Math.min(...idxs) : null;
      s.max_resulting = idxs.length ? Math.max(...idxs) : null;
    }
    // Count pending moves (differ from original).
    let pending = 0;
    for (const pid of Object.keys(movedMap)) {
      if ((movedMap[pid] || null) !== (originalMap[pid] || null)) pending++;
    }
    return { summary, unassigned, pending };
  }, [preview, movedMap, originalMap]);

  const savePendingMoves = async () => {
    if (!derivedView || derivedView.pending === 0) return;
    setSavingMoves(true);
    try {
      const diffs = Object.entries(movedMap).filter(
        ([pid, cid]) => (cid || null) !== (originalMap[pid] || null),
      );
      let ok = 0;
      for (const [pid, cid] of diffs) {
        const r = await fetch(`${API}/club/tournaments/${tid}/players/${pid}`, {
          method: "PUT", credentials: "include", headers: AUTH(),
          body: JSON.stringify({ category_id: cid || null }),
        });
        if (r.ok) ok++;
      }
      toast.success(`${ok} jugador${ok === 1 ? "" : "es"} movido${ok === 1 ? "" : "s"}`);
      setOriginalMap({ ...movedMap });
      fetchP(); onChange?.();
    } catch (e) {
      toast.error("Error guardando cambios");
    } finally { setSavingMoves(false); }
  };

  const assignMode = tournament.category_assignment?.mode;
  const modeLabel = assignMode === "sequential" ? "Por orden de HCP"
    : assignMode === "mixed" ? "Mixto" : "Por rango de HCP";

  // Filter chips: count per bucket.
  const counts = useMemo(() => {
    const c = { all: players.length, none: 0 };
    for (const p of players) {
      if (!p.category_id) c.none += 1;
      else c[p.category_id] = (c[p.category_id] || 0) + 1;
    }
    return c;
  }, [players]);
  const filteredPlayers = useMemo(() => {
    if (catFilter === "all") return players;
    if (catFilter === "none") return players.filter((p) => !p.category_id);
    return players.filter((p) => p.category_id === catFilter);
  }, [players, catFilter]);

  // Selection helpers derived from current filter view.
  const visibleIds = useMemo(() => filteredPlayers.map((p) => p.player_id), [filteredPlayers]);
  const visibleSelectedCount = useMemo(
    () => visibleIds.reduce((n, id) => n + (selected.has(id) ? 1 : 0), 0),
    [visibleIds, selected],
  );
  const allVisibleSelected = visibleIds.length > 0 && visibleSelectedCount === visibleIds.length;
  const toggleAllVisible = () => {
    setSelected((s) => {
      const n = new Set(s);
      if (allVisibleSelected) { visibleIds.forEach((id) => n.delete(id)); }
      else { visibleIds.forEach((id) => n.add(id)); }
      return n;
    });
  };

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <span className="text-sm text-[#64748B]" data-testid="players-count">{players.length} jugadores</span>
          {hasAssignment && (
            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-[#1B4D3E]/10 text-[#1B4D3E]">
              {modeLabel}
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canWrite && hasAssignment && players.length > 0 && (
            <>
              <Button
                size="sm" variant="outline"
                onClick={runPreview} disabled={previewing}
                data-testid="preview-cats-btn"
              >
                {previewing ? "Simulando…" : "Vista previa"}
              </Button>
              <Button
                size="sm"
                className="bg-[#1B4D3E] hover:bg-[#163F33] text-white"
                onClick={applyAssignment} disabled={applying}
                data-testid="apply-cats-btn"
              >
                {applying ? "Aplicando…" : "Aplicar algoritmo"}
              </Button>
            </>
          )}
          {canWrite && (
            <Button size="sm" onClick={() => setShowAdd(true)} className="bg-[#1B4D3E] hover:bg-[#163F33] text-white" data-testid="add-players-btn">
              <Plus className="w-4 h-4 mr-1" /> Agregar Jugadores
            </Button>
          )}
        </div>
      </div>

      {preview && derivedView && (
        <div className="mb-4 rounded-xl border border-[#1B4D3E]/20 bg-[#1B4D3E]/5 p-4" data-testid="preview-panel">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-[#1B4D3E]">Vista previa de categorías</p>
              <p className="text-[11px] text-[#64748B] mt-0.5">
                Arrastra jugadores entre tarjetas para ajustar. {preview.total_players} jugadores totales.
                {derivedView.pending > 0 && (
                  <span className="ml-1 text-amber-700 font-semibold">
                    · {derivedView.pending} cambio{derivedView.pending === 1 ? "" : "s"} pendiente{derivedView.pending === 1 ? "" : "s"}
                  </span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {derivedView.pending > 0 && (
                <Button
                  size="sm"
                  className="bg-[#1B4D3E] hover:bg-[#163F33] text-white"
                  onClick={savePendingMoves} disabled={savingMoves}
                  data-testid="save-moves-btn"
                >
                  {savingMoves ? "Guardando…" : `Guardar ${derivedView.pending} cambio${derivedView.pending === 1 ? "" : "s"}`}
                </Button>
              )}
              <button
                onClick={() => { setPreview(null); setMovedMap({}); setOriginalMap({}); }}
                className="text-[#64748B] hover:text-[#0F172A]" aria-label="Cerrar preview"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="preview-summary">
            {derivedView.summary.map((s) => (
              <PreviewCard
                key={s.category_id}
                summary={s}
                isDragOver={dragOver === s.category_id}
                onDragOver={(e) => { e.preventDefault(); setDragOver(s.category_id); }}
                onDragLeave={() => setDragOver((d) => (d === s.category_id ? null : d))}
                onDrop={() => dropOn(s.category_id)}
                onChipDragStart={setDragPid}
                onChipDragEnd={() => setDragPid(null)}
                canDrag={canWrite}
              />
            ))}
            <UnassignedCard
              players={derivedView.unassigned}
              isDragOver={dragOver === "__none__"}
              onDragOver={(e) => { e.preventDefault(); setDragOver("__none__"); }}
              onDragLeave={() => setDragOver((d) => (d === "__none__" ? null : d))}
              onDrop={() => dropOn(null)}
              onChipDragStart={setDragPid}
              onChipDragEnd={() => setDragPid(null)}
              canDrag={canWrite}
            />
          </div>

          {preview.warnings?.map((w, i) => (
            <p key={i} className="text-[11px] text-amber-700 mt-2">⚠ {w}</p>
          ))}
        </div>
      )}

      {showAdd && <AddPlayersModal tournament={tournament} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); fetchP(); onChange?.(); }} />}

      {canWrite && selected.size > 0 && (
        <div
          className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2"
          data-testid="bulk-action-bar"
        >
          <span className="text-sm font-semibold text-red-700" data-testid="bulk-selected-count">
            {selected.size} seleccionado{selected.size === 1 ? "" : "s"}
          </span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={clearSelection} data-testid="bulk-clear-btn">
              Limpiar
            </Button>
            <Button
              size="sm"
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => setShowBulkDelete(true)}
              disabled={bulkDeleting}
              data-testid="bulk-delete-btn"
            >
              <Trash2 className="w-4 h-4 mr-1" /> Eliminar seleccionados
            </Button>
          </div>
        </div>
      )}

      <ConfirmDeleteModal
        open={showBulkDelete}
        onOpenChange={setShowBulkDelete}
        entityName="ELIMINAR"
        entityLabel={`${selected.size} jugador${selected.size === 1 ? "" : "es"}`}
        description={`Se eliminarán ${selected.size} jugador${selected.size === 1 ? "" : "es"} del torneo. Los que tengan scores capturados serán omitidos automáticamente.`}
        onConfirm={bulkDelete}
        testId="bulk-delete-confirm"
      />

      {/* Filter chips */}
      {players.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid="players-filter">
          <button
            onClick={() => setCatFilter("all")}
            className={`px-3 h-8 rounded-full text-xs font-semibold border transition-colors ${
              catFilter === "all" ? "bg-[#1B4D3E] text-white border-[#1B4D3E]" : "bg-white text-[#64748B] border-gray-200"
            }`}
            data-testid="filter-cat-all"
          >
            Todos <span className="opacity-70 ml-1">{counts.all}</span>
          </button>
          {cats.map((c) => (
            <button
              key={c.category_id}
              onClick={() => setCatFilter(c.category_id)}
              className={`px-3 h-8 rounded-full text-xs font-semibold border transition-colors ${
                catFilter === c.category_id ? "bg-[#1B4D3E] text-white border-[#1B4D3E]" : "bg-white text-[#64748B] border-gray-200"
              }`}
              data-testid={`filter-cat-${c.category_id}`}
            >
              {c.name} <span className="opacity-70 ml-1">{counts[c.category_id] || 0}</span>
            </button>
          ))}
          {counts.none > 0 && (
            <button
              onClick={() => setCatFilter("none")}
              className={`px-3 h-8 rounded-full text-xs font-semibold border transition-colors ${
                catFilter === "none" ? "bg-amber-500 text-white border-amber-500" : "bg-white text-amber-700 border-amber-200"
              }`}
              data-testid="filter-cat-none"
            >
              Sin asignar <span className="opacity-70 ml-1">{counts.none}</span>
            </button>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-8"><div className="w-6 h-6 border-3 border-[#1B4D3E] border-t-transparent rounded-full animate-spin" /></div>
      ) : players.length === 0 ? (
        <Card className="card-default"><CardContent className="py-12 text-center text-[#94A3B8]">Sin jugadores inscritos</CardContent></Card>
      ) : filteredPlayers.length === 0 ? (
        <Card className="card-default"><CardContent className="py-12 text-center text-[#94A3B8]">No hay jugadores en esta categoría</CardContent></Card>
      ) : (
        <Card className="card-default">
          <CardContent className="p-0">
            <table className="w-full text-sm" data-testid="players-table">
              <thead className="bg-gray-50">
                <tr>
                  {canWrite && (
                    <th className="w-10 px-3 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={allVisibleSelected}
                        onChange={toggleAllVisible}
                        aria-label="Seleccionar todos"
                        data-testid="select-all-players"
                        className="rounded border-gray-300 accent-[#1B4D3E] cursor-pointer"
                      />
                    </th>
                  )}
                  <th className="text-left px-4 py-2 text-xs font-semibold text-[#64748B]">JUGADOR</th>
                  <th className="text-center px-4 py-2 text-xs font-semibold text-[#64748B]">INDEX</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold text-[#64748B]">CATEGORIA</th>
                  {canWrite && <th className="w-16"></th>}
                </tr>
              </thead>
              <tbody>
                {filteredPlayers.map((p) => (
                  <tr key={p.player_id} className="border-t border-gray-50 hover:bg-gray-50/50" data-testid={`player-${p.player_id}`}>
                    {canWrite && (
                      <td className="px-3 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={selected.has(p.player_id)}
                          onChange={() => toggleOne(p.player_id)}
                          aria-label={`Seleccionar ${p.name}`}
                          data-testid={`select-player-${p.player_id}`}
                          className="rounded border-gray-300 accent-[#1B4D3E] cursor-pointer"
                        />
                      </td>
                    )}
                    <td className="px-4 py-2 font-medium text-[#0F172A]">
                      {p.name}
                      {!p.member_id && (
                        <span className="ml-2 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 tracking-wide">
                          Invitado
                        </span>
                      )}
                    </td>
                    <td className="text-center px-4 py-2 text-[#64748B]">{p.handicap_index ?? "—"}</td>
                    <td className="px-4 py-2 text-[#64748B]">
                      {canWrite ? (
                        <select
                          value={p.category_id || ""}
                          onChange={(e) => moveCategory(p.player_id, e.target.value)}
                          className="h-8 rounded border border-gray-200 bg-white px-2 text-xs font-medium focus:outline-none focus:border-[#1B4D3E]"
                          data-testid={`move-cat-${p.player_id}`}
                        >
                          <option value="">— Sin asignar —</option>
                          {cats.map((c) => (
                            <option key={c.category_id} value={c.category_id}>{c.name}</option>
                          ))}
                        </select>
                      ) : (
                        p.category_name || "—"
                      )}
                    </td>
                    {canWrite && (
                      <td className="px-4 py-2 text-right whitespace-nowrap">
                        {!p.member_id && (
                          <button
                            onClick={() => promoteToMember(p.player_id)}
                            className="text-[11px] font-semibold px-2 py-1 rounded bg-[#1B4D3E]/10 text-[#1B4D3E] hover:bg-[#1B4D3E]/20 mr-2"
                            data-testid={`promote-${p.player_id}`}
                            title="Agregar al padrón del club"
                          >
                            + Padrón
                          </button>
                        )}
                        <button onClick={() => remove(p.player_id)} className="text-red-500 hover:text-red-700 align-middle" data-testid={`del-player-${p.player_id}`}><Trash2 className="w-4 h-4" /></button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </>
  );
}

function PreviewCard({ summary: s, isDragOver, onDragOver, onDragLeave, onDrop, onChipDragStart, onChipDragEnd, canDrag }) {
  return (
    <div
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={`bg-white rounded-lg border p-3 transition-colors ${
        isDragOver ? "border-[#1B4D3E] bg-[#1B4D3E]/5" : "border-gray-100"
      }`}
      data-testid={`preview-card-${s.category_id}`}
    >
      <div className="flex items-center justify-between mb-1">
        <p className="text-sm font-bold text-[#0F172A]">{s.name}</p>
        <span className="text-[10px] font-bold uppercase tracking-wide text-[#94A3B8]">
          {s.rule_type === "SEQUENTIAL_BLOCK" ? `Bloque ${s.block_size || ""}` : "Rango"}
        </span>
      </div>
      <p className="text-[11px] text-[#64748B] mb-2">
        {s.count} jugador{s.count === 1 ? "" : "es"}
        {s.min_resulting !== null && s.max_resulting !== null && (
          <> · Index {s.min_resulting} → {s.max_resulting}</>
        )}
      </p>
      <div className="flex flex-wrap gap-1 min-h-[24px]">
        {(s.players || []).map((p) => (
          <PlayerChip key={p.player_id} p={p} onDragStart={onChipDragStart} onDragEnd={onChipDragEnd} canDrag={canDrag} />
        ))}
      </div>
    </div>
  );
}

function UnassignedCard({ players, isDragOver, onDragOver, onDragLeave, onDrop, onChipDragStart, onChipDragEnd, canDrag }) {
  return (
    <div
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={`rounded-lg border p-3 transition-colors ${
        isDragOver ? "border-amber-500 bg-amber-50" : "border-dashed border-amber-300 bg-amber-50/40"
      }`}
      data-testid="preview-card-unassigned"
    >
      <div className="flex items-center justify-between mb-1">
        <p className="text-sm font-bold text-amber-800">Sin asignar</p>
      </div>
      <p className="text-[11px] text-amber-700 mb-2">
        {players.length} jugador{players.length === 1 ? "" : "es"}
      </p>
      <div className="flex flex-wrap gap-1 min-h-[24px]">
        {players.map((p) => (
          <PlayerChip key={p.player_id} p={p} onDragStart={onChipDragStart} onDragEnd={onChipDragEnd} canDrag={canDrag} amber />
        ))}
      </div>
    </div>
  );
}

function PlayerChip({ p, onDragStart, onDragEnd, canDrag, amber }) {
  return (
    <span
      draggable={canDrag}
      onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; onDragStart(p.player_id); }}
      onDragEnd={onDragEnd}
      className={`text-[11px] px-2 py-0.5 rounded-full border select-none ${canDrag ? "cursor-grab active:cursor-grabbing" : ""} ${
        amber
          ? "bg-white text-amber-800 border-amber-200"
          : "bg-gray-50 text-[#0F172A] border-gray-200"
      }`}
      data-testid={`chip-${p.player_id}`}
    >
      {p.name}{p.handicap_index != null ? ` · ${p.handicap_index}` : ""}
    </span>
  );
}

function AddPlayersModal({ tournament, onClose, onSaved }) {
  const [members, setMembers] = useState([]);
  const [tournPlayers, setTournPlayers] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("base"); // "base" | "manual" | "csv"
  const [manual, setManual] = useState({ name: "", handicap_index: "", category_id: "" });
  const [saving, setSaving] = useState(false);
  const [csvText, setCsvText] = useState("");
  const [csvBusy, setCsvBusy] = useState(false);
  const [preview, setPreview] = useState(null); // { matched, duplicates, already_enrolled, guests, total_rows }
  const [pick, setPick] = useState({}); // per-row picks: { matched: {i: bool}, guests: {i:bool}, dups: {i: member_id|null}, hcp_over: {i: bool} }

  useEffect(() => {
    fetch(`${API}/club/players`, { credentials: "include", headers: AUTH() })
      .then((r) => r.ok ? r.json() : []).then(setMembers).catch(() => {});
    fetch(`${API}/club/tournaments/${tournament.tournament_id}/players`, { credentials: "include", headers: AUTH() })
      .then((r) => r.ok ? r.json() : []).then(setTournPlayers).catch(() => {});
  }, [tournament.tournament_id]);

  const fullName = (m) => (m.name || `${m.first_name || ""} ${m.last_name || ""}`).trim();
  const existingMemberIds = new Set(tournPlayers.filter((p) => p.member_id).map((p) => p.member_id));
  const filtered = members.filter((m) =>
    !existingMemberIds.has(m.member_id) &&
    fullName(m).toLowerCase().includes(search.toLowerCase())
  );

  const toggle = (mid) => setSelectedIds((p) => {
    const next = new Set(p);
    if (next.has(mid)) next.delete(mid); else next.add(mid);
    return next;
  });

  const handleSaveFromBase = async () => {
    if (selectedIds.size === 0) return toast.error("Selecciona al menos uno");
    setSaving(true);
    try {
      const payload = {
        players: members.filter((m) => selectedIds.has(m.member_id)).map((m) => ({
          name: fullName(m), handicap_index: m.handicap_index, member_id: m.member_id,
        })),
      };
      const res = await fetch(`${API}/club/tournaments/${tournament.tournament_id}/players`, {
        method: "POST", credentials: "include", headers: AUTH(), body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Error");
      toast.success(`${data.created} agregados`);
      onSaved();
    } catch (err) { toast.error(err.message || "Error"); }
    finally { setSaving(false); }
  };

  const handleSaveManual = async () => {
    if (!manual.name.trim()) return toast.error("Nombre requerido");
    setSaving(true);
    try {
      const payload = {
        players: [{
          name: manual.name,
          handicap_index: manual.handicap_index ? parseFloat(manual.handicap_index) : null,
          category_id: manual.category_id || null,
        }],
      };
      const res = await fetch(`${API}/club/tournaments/${tournament.tournament_id}/players`, {
        method: "POST", credentials: "include", headers: AUTH(), body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Error");
      toast.success("Agregado");
      onSaved();
    } catch (err) { toast.error(err.message || "Error"); }
    finally { setSaving(false); }
  };

  // ── CSV: RFC-4180 parser (Papaparse) so quoted commas / decimal commas work ──
  const parseCSV = (text) => {
    const parsed = Papa.parse(text.trim(), { header: true, skipEmptyLines: true });
    return (parsed.data || []).map((raw) => {
      const obj = {};
      for (const rawKey of Object.keys(raw)) {
        const h = String(rawKey || "").trim().toLowerCase().replace(/\s+/g, "_");
        const key =
          h === "nombre" ? "first_name"
          : h === "apellido" ? "last_name"
          : h === "index" ? "handicap_index"
          : (h === "socio" || h === "no_socio" || h === "numero_socio") ? "member_number"
          : h === "correo" ? "email"
          : h === "telefono" ? "phone"
          : (h === "notas" || h === "observaciones") ? "notes"
          : (h === "sexo" || h === "genero") ? "sex"
          : (h === "senior" || h === "es_senior") ? "is_senior"
          : h;
        let v = (raw[rawKey] ?? "").toString().trim();
        if (key === "handicap_index") {
          // Support comma decimal separator (e.g. "1,4" → "1.4"). Also golf's
          // "+" prefix means better than scratch, stored as negative: "+2" → "-2".
          v = v.replace(",", ".").replace(/^\+/, "-");
        }
        if (key === "sex") {
          const up = v.toUpperCase();
          if (up.startsWith("M") || up.startsWith("H")) v = "M";
          else if (up.startsWith("F") || up.startsWith("MU")) v = "F";
          else v = "";
        }
        if (key === "is_senior") {
          const low = v.toLowerCase();
          v = ["1", "true", "si", "sí", "yes", "y", "x", "senior"].includes(low) ? "true" : "";
        }
        obj[key] = v;
      }
      return obj;
    });
  };

  const rowDisplayName = (r) => `${r.first_name || ""} ${r.last_name || ""}`.trim() || r.name || "—";

  const runCsvPreview = async () => {
    const rows = parseCSV(csvText);
    if (rows.length === 0) return toast.error("No se encontraron filas válidas");
    setCsvBusy(true);
    try {
      const res = await fetch(`${API}/club/tournaments/${tournament.tournament_id}/players/csv-preview`, {
        method: "POST", credentials: "include", headers: AUTH(), body: JSON.stringify({ rows }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Error");
      setPreview(data);
      // Default: check all matched + all guests; dups → no pick (must be resolved).
      const pMatched = {}; data.matched.forEach((_, i) => (pMatched[i] = true));
      const pGuests = {}; data.guests.forEach((_, i) => (pGuests[i] = true));
      const pHcpOver = {}; data.matched.forEach((_, i) => (pHcpOver[i] = false));
      const pDups = {}; data.duplicates.forEach((_, i) => (pDups[i] = null));
      setPick({ matched: pMatched, guests: pGuests, dups: pDups, hcp_over: pHcpOver });
    } catch (err) { toast.error(err.message || "Error"); }
    finally { setCsvBusy(false); }
  };

  const confirmCsv = async () => {
    if (!preview) return;
    const players = [];

    // Matched (padrón)
    preview.matched.forEach((m, i) => {
      if (!pick.matched?.[i]) return;
      const hcpFromCsv = m.row.handicap_index;
      const hcp = pick.hcp_over?.[i] && hcpFromCsv != null ? hcpFromCsv : (m.member.handicap_index ?? hcpFromCsv);
      players.push({ name: `${m.member.first_name} ${m.member.last_name}`.trim(), handicap_index: hcp, member_id: m.member.member_id });
    });

    // Duplicates — resolved
    preview.duplicates.forEach((d, i) => {
      const chosen = pick.dups?.[i];
      if (!chosen) return; // unresolved → skip
      const m = d.candidates.find((c) => c.member_id === chosen);
      if (!m) return;
      players.push({ name: `${m.first_name} ${m.last_name}`.trim(), handicap_index: m.handicap_index ?? d.row.handicap_index, member_id: m.member_id });
    });

    // Guests
    preview.guests.forEach((g, i) => {
      if (!pick.guests?.[i]) return;
      players.push({
        name: rowDisplayName(g.row),
        handicap_index: g.row.handicap_index,
        member_id: null,
        sex: g.row.sex || null,
        is_senior: g.row.is_senior === "true" || g.row.is_senior === true,
      });
    });

    if (players.length === 0) return toast.error("Nada por agregar. Marca al menos una fila.");

    // Optional padrón HCP overwrites via PATCH (best-effort, non-blocking).
    const hcpUpdates = preview.matched
      .map((m, i) => ({ m, i }))
      .filter(({ i }) => pick.matched?.[i] && pick.hcp_over?.[i])
      .filter(({ m }) => m.padron_hcp_diff && m.row.handicap_index != null);

    setSaving(true);
    try {
      const res = await fetch(`${API}/club/tournaments/${tournament.tournament_id}/players`, {
        method: "POST", credentials: "include", headers: AUTH(), body: JSON.stringify({ players }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Error");
      // Fire-and-forget padrón overwrites.
      await Promise.all(hcpUpdates.map(({ m }) =>
        fetch(`${API}/club/players/${m.member.member_id}`, {
          method: "PUT", credentials: "include", headers: AUTH(),
          body: JSON.stringify({ handicap_index: m.row.handicap_index }),
        }).catch(() => {})
      ));
      toast.success(`${data.created} agregados`);
      onSaved();
    } catch (err) { toast.error(err.message || "Error"); }
    finally { setSaving(false); }
  };

  const isCsvPreview = tab === "csv" && preview;
  const modalMax = isCsvPreview ? "max-w-3xl" : "max-w-lg";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className={`bg-white rounded-2xl shadow-2xl w-full ${modalMax} max-h-[92vh] flex flex-col`} data-testid="add-players-modal">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-bold text-[#0F172A]">Agregar Jugadores</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-[#64748B]" /></button>
        </div>
        <div className="flex gap-1 px-6 pt-3">
          <button onClick={() => setTab("base")} className={`px-3 py-1.5 text-sm font-medium rounded ${tab === "base" ? "bg-[#1B4D3E] text-white" : "text-[#64748B]"}`} data-testid="tab-from-base">Del padrón</button>
          <button onClick={() => setTab("manual")} className={`px-3 py-1.5 text-sm font-medium rounded ${tab === "manual" ? "bg-[#1B4D3E] text-white" : "text-[#64748B]"}`} data-testid="tab-manual">Manual</button>
          <button onClick={() => setTab("csv")} className={`px-3 py-1.5 text-sm font-medium rounded ${tab === "csv" ? "bg-[#1B4D3E] text-white" : "text-[#64748B]"}`} data-testid="tab-csv">CSV</button>
        </div>
        <div className="p-6 overflow-y-auto flex-1">
          {tab === "base" && (
            <>
              <Input placeholder="Buscar..." value={search} onChange={(e) => setSearch(e.target.value)} className="mb-3" data-testid="player-search" />
              {filtered.length === 0 ? (
                <p className="text-sm text-[#94A3B8] text-center py-6">{members.length === 0 ? "No hay jugadores en el padrón" : "Todos ya están inscritos"}</p>
              ) : (
                <div className="space-y-1 max-h-96 overflow-y-auto" data-testid="members-list">
                  {filtered.map((m) => (
                    <label key={m.member_id} className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer ${selectedIds.has(m.member_id) ? "bg-[#1B4D3E]/10" : "hover:bg-gray-50"}`} data-testid={`member-${m.member_id}`}>
                      <input type="checkbox" checked={selectedIds.has(m.member_id)} onChange={() => toggle(m.member_id)} className="w-4 h-4 rounded border-gray-300" />
                      <div className="flex-1">
                        <div className="font-medium text-sm text-[#0F172A]">{fullName(m)}</div>
                        <div className="text-xs text-[#94A3B8]">Index: {m.handicap_index ?? "—"}</div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </>
          )}

          {tab === "manual" && (
            <div className="space-y-3">
              <div><Label className="text-sm">Nombre *</Label><Input value={manual.name} onChange={(e) => setManual({ ...manual, name: e.target.value })} data-testid="manual-name" /></div>
              <div><Label className="text-sm">Handicap Index</Label><Input type="number" step="0.1" value={manual.handicap_index} onChange={(e) => setManual({ ...manual, handicap_index: e.target.value })} data-testid="manual-index" /></div>
              {tournament.categories?.length > 0 && (
                <div>
                  <Label className="text-sm">Categoría (opcional, sino se calcula por index)</Label>
                  <Select value={manual.category_id} onValueChange={(v) => setManual({ ...manual, category_id: v === "auto" ? "" : v })}>
                    <SelectTrigger data-testid="manual-cat"><SelectValue placeholder="Auto" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Auto (por index)</SelectItem>
                      {tournament.categories.map((c) => <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}

          {tab === "csv" && !preview && (
            <div className="space-y-3" data-testid="csv-upload">
              <p className="text-xs text-[#64748B]">
                Sube un CSV con las mismas columnas del padrón: <span className="font-mono">nombre,apellido,index,socio,correo,telefono,sexo,senior,notas</span>.
              </p>
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  const r = new FileReader();
                  r.onload = () => setCsvText(String(r.result || ""));
                  r.readAsText(f);
                }}
                className="text-sm"
                data-testid="csv-file-input"
              />
              <textarea
                value={csvText}
                onChange={(e) => setCsvText(e.target.value)}
                placeholder="… o pega aquí el contenido CSV"
                className="w-full h-40 border border-gray-200 rounded-lg p-2 text-xs font-mono"
                data-testid="csv-textarea"
              />
            </div>
          )}

          {tab === "csv" && preview && (
            <div className="space-y-5" data-testid="csv-preview">
              {/* Matched */}
              {preview.matched.length > 0 && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-[#1B4D3E] mb-2">
                    ✓ Miembros del padrón · {preview.matched.length}
                  </p>
                  <div className="space-y-1 max-h-56 overflow-y-auto">
                    {preview.matched.map((m, i) => (
                      <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#1B4D3E]/5" data-testid={`preview-matched-${i}`}>
                        <input type="checkbox" checked={!!pick.matched?.[i]} onChange={(e) => setPick((p) => ({ ...p, matched: { ...p.matched, [i]: e.target.checked } }))} className="w-4 h-4" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#0F172A] truncate">{m.member.first_name} {m.member.last_name}</p>
                          <p className="text-[11px] text-[#64748B]">
                            {m.member.email || "sin correo"} · Padrón HCP {m.member.handicap_index ?? "—"}
                          </p>
                        </div>
                        {m.padron_hcp_diff && (
                          <label className="flex items-center gap-1 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5" title="Sobrescribir el HCP del padrón con el del CSV">
                            <input type="checkbox" checked={!!pick.hcp_over?.[i]} onChange={(e) => setPick((p) => ({ ...p, hcp_over: { ...p.hcp_over, [i]: e.target.checked } }))} className="w-3 h-3" />
                            HCP CSV {m.padron_hcp_diff.csv} vs padrón {m.padron_hcp_diff.padron ?? "—"}
                          </label>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Duplicates */}
              {preview.duplicates.length > 0 && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-red-700 mb-2">
                    ⚠ Duplicados detectados · {preview.duplicates.length}
                  </p>
                  <p className="text-[11px] text-[#64748B] mb-2">La fila del CSV coincide con más de un miembro. Elige cuál es el correcto.</p>
                  <div className="space-y-2">
                    {preview.duplicates.map((d, i) => (
                      <div key={i} className="p-3 rounded-lg border border-red-100 bg-red-50/40" data-testid={`preview-dup-${i}`}>
                        <p className="text-sm font-semibold text-[#0F172A] mb-1">CSV: {rowDisplayName(d.row)} · Index {d.row.handicap_index ?? "—"}</p>
                        <div className="space-y-1">
                          {d.candidates.map((c) => (
                            <label key={c.member_id} className="flex items-center gap-2 text-xs bg-white rounded px-2 py-1 border border-red-100 cursor-pointer">
                              <input
                                type="radio"
                                name={`dup-${i}`}
                                checked={pick.dups?.[i] === c.member_id}
                                onChange={() => setPick((p) => ({ ...p, dups: { ...p.dups, [i]: c.member_id } }))}
                              />
                              <span className="font-medium">{c.first_name} {c.last_name}</span>
                              <span className="text-[#64748B]">· {c.email || "sin correo"} · Socio {c.member_number || "—"} · HCP {c.handicap_index ?? "—"}</span>
                            </label>
                          ))}
                          <label className="flex items-center gap-2 text-xs text-[#94A3B8] px-2 py-1 cursor-pointer">
                            <input type="radio" name={`dup-${i}`} checked={pick.dups?.[i] == null} onChange={() => setPick((p) => ({ ...p, dups: { ...p.dups, [i]: null } }))} />
                            Ninguno · omitir esta fila
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Guests */}
              {preview.guests.length > 0 && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-amber-700 mb-2">
                    ⛳ Invitados (no están en el padrón) · {preview.guests.length}
                  </p>
                  <p className="text-[11px] text-[#64748B] mb-2">Se inscriben al torneo como invitados. Después podrás promoverlos al padrón desde la lista de jugadores.</p>
                  <div className="space-y-1 max-h-56 overflow-y-auto">
                    {preview.guests.map((g, i) => (
                      <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50" data-testid={`preview-guest-${i}`}>
                        <input type="checkbox" checked={!!pick.guests?.[i]} onChange={(e) => setPick((p) => ({ ...p, guests: { ...p.guests, [i]: e.target.checked } }))} className="w-4 h-4" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#0F172A] truncate">{rowDisplayName(g.row)}</p>
                          <p className="text-[11px] text-[#64748B]">Index {g.row.handicap_index ?? "—"}{g.row.email ? ` · ${g.row.email}` : ""}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Already enrolled */}
              {preview.already_enrolled.length > 0 && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-[#94A3B8] mb-2">
                    · Ya inscritos · {preview.already_enrolled.length}
                  </p>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {preview.already_enrolled.map((a, i) => (
                      <div key={i} className="text-xs text-[#64748B] px-3 py-1 rounded bg-gray-50">
                        {a.member.first_name} {a.member.last_name}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex gap-2">
          <Button variant="outline" className="flex-1" onClick={onClose}>Cancelar</Button>
          {tab === "manual" && (
            <Button className="flex-1 bg-[#1B4D3E] hover:bg-[#163F33] text-white" onClick={handleSaveManual} disabled={saving} data-testid="save-manual-btn">{saving ? "..." : "Agregar"}</Button>
          )}
          {tab === "base" && (
            <Button className="flex-1 bg-[#1B4D3E] hover:bg-[#163F33] text-white" onClick={handleSaveFromBase} disabled={saving || selectedIds.size === 0} data-testid="save-base-btn">{saving ? "..." : `Agregar (${selectedIds.size})`}</Button>
          )}
          {tab === "csv" && !preview && (
            <Button className="flex-1 bg-[#1B4D3E] hover:bg-[#163F33] text-white" onClick={runCsvPreview} disabled={csvBusy || !csvText.trim()} data-testid="csv-preview-btn">
              {csvBusy ? "Procesando…" : "Revisar CSV"}
            </Button>
          )}
          {tab === "csv" && preview && (
            <>
              <Button variant="outline" onClick={() => setPreview(null)} data-testid="csv-back-btn">Volver</Button>
              <Button className="flex-1 bg-[#1B4D3E] hover:bg-[#163F33] text-white" onClick={confirmCsv} disabled={saving} data-testid="csv-confirm-btn">
                {saving ? "Agregando…" : "Confirmar e inscribir"}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function TeeTimesTab({ tournament, canWrite }) {
  const [teetimes, setTeetimes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGen, setShowGen] = useState(false);
  const [slots, setSlots] = useState([]);
  const [genForm, setGenForm] = useState({
    round_num: 1, selectedSlotIds: new Set(), players_per_group: 4, sort_by: "random", category_id: "",
  });
  const [showShotgun, setShowShotgun] = useState(false);
  const [sgForm, setSgForm] = useState({
    round_num: 1, players_per_group: 4, sort_by: "random", category_id: "", time: "08:00",
    holes: new Set(Array.from({ length: 18 }, (_, i) => i + 1)),
  });
  const [catFilter, setCatFilter] = useState("all");
  const [roundFilter, setRoundFilter] = useState("all");
  const [dayFilter, setDayFilter] = useState("all");
  const [nameQuery, setNameQuery] = useState("");
  const tid = tournament.tournament_id;
  const cats = tournament.categories || [];

  const fetchTT = useCallback(async () => {
    try {
      const res = await fetch(`${API}/club/tournaments/${tid}/teetimes`, { credentials: "include", headers: AUTH() });
      if (res.ok) setTeetimes(await res.json());
    } finally { setLoading(false); }
  }, [tid]);
  useEffect(() => { fetchTT(); }, [fetchTT]);

  const openGen = async () => {
    setShowGen(true);
    // Prefill category to the first one when the tournament has categories,
    // since a "general" tee-time is no longer allowed.
    if (cats.length > 0) {
      setGenForm((p) => ({ ...p, category_id: p.category_id || cats[0].category_id }));
    }
    try {
      const res = await fetch(`${API}/club/tee-slots`, { credentials: "include", headers: AUTH() });
      if (res.ok) {
        const s = await res.json();
        setSlots(s);
        setGenForm((p) => ({ ...p, selectedSlotIds: new Set(s.map((x) => x.slot_id)) }));
      }
    } catch { /* silent */ }
  };

  const toggleSlot = (sid) => setGenForm((p) => {
    const n = new Set(p.selectedSlotIds);
    if (n.has(sid)) n.delete(sid); else n.add(sid);
    return { ...p, selectedSlotIds: n };
  });

  const handleGen = async () => {
    if (genForm.selectedSlotIds.size === 0) return toast.error("Selecciona horarios");
    try {
      const res = await fetch(`${API}/club/tournaments/${tid}/teetimes/generate`, {
        method: "POST", credentials: "include", headers: AUTH(),
        body: JSON.stringify({
          round_num: parseInt(genForm.round_num),
          slot_ids: Array.from(genForm.selectedSlotIds),
          players_per_group: genForm.players_per_group,
          sort_by: genForm.sort_by,
          category_id: genForm.category_id || null,
        }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || "Error");
      toast.success("Tee times generados");
      setShowGen(false); fetchTT();
    } catch (err) { toast.error(err.message || "Error"); }
  };

  const openShotgun = () => {
    setShowShotgun(true);
    if (cats.length > 0) setSgForm((p) => ({ ...p, category_id: p.category_id || cats[0].category_id }));
  };
  const toggleSgHole = (h) => setSgForm((p) => {
    const n = new Set(p.holes);
    if (n.has(h)) n.delete(h); else n.add(h);
    return { ...p, holes: n };
  });
  const handleShotgun = async () => {
    if (sgForm.holes.size === 0) return toast.error("Selecciona al menos un hoyo");
    if (!sgForm.time) return toast.error("Indica la hora de salida");
    try {
      const res = await fetch(`${API}/club/tournaments/${tid}/teetimes/generate-shotgun`, {
        method: "POST", credentials: "include", headers: AUTH(),
        body: JSON.stringify({
          round_num: parseInt(sgForm.round_num),
          players_per_group: sgForm.players_per_group,
          sort_by: sgForm.sort_by,
          category_id: sgForm.category_id || null,
          time: sgForm.time,
          holes: Array.from(sgForm.holes).sort((a, b) => a - b),
        }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || "Error");
      toast.success(`Escopetazo generado (${d.total_groups} grupos)`);
      setShowShotgun(false); fetchTT();
    } catch (err) { toast.error(err.message || "Error"); }
  };

  const handlePublish = async (ttId, status) => {
    try {
      await fetch(`${API}/club/tournaments/${tid}/teetimes/${ttId}`, {
        method: "PUT", credentials: "include", headers: AUTH(),
        body: JSON.stringify({ status: status === "published" ? "draft" : "published" }),
      });
      toast.success("Actualizado"); fetchTT();
    } catch { toast.error("Error"); }
  };

  // ---- Quick generate from calendar ----
  const [showQuickGen, setShowQuickGen] = useState(false);
  const [qgState, setQgState] = useState({}); // {"cat_id_r_num": "random"|"handicap"|"result"}
  const catsWithSchedule = cats.filter((c) => (c.round_schedules || []).some((r) => (r.slot_ids || []).length > 0));
  const setQgSort = (cid, rn, v) => setQgState((s) => ({ ...s, [`${cid}_${rn}`]: v }));
  const quickGen = async () => {
    let ok = 0, fail = 0;
    for (const c of catsWithSchedule) {
      for (const r of (c.round_schedules || [])) {
        if ((r.slot_ids || []).length === 0) continue;
        const sortBy = qgState[`${c.category_id}_${r.round_num}`]
          || (r.round_num === 1 ? "random" : "by_leaderboard");
        try {
          const res = await fetch(`${API}/club/tournaments/${tid}/teetimes/generate`, {
            method: "POST", credentials: "include", headers: AUTH(),
            body: JSON.stringify({
              round_num: r.round_num,
              slot_ids: r.slot_ids,
              players_per_group: 4,
              sort_by: sortBy,
              category_id: c.category_id,
            }),
          });
          if (res.ok) ok += 1; else fail += 1;
        } catch { fail += 1; }
      }
    }
    if (ok > 0) toast.success(`${ok} tee time${ok === 1 ? "" : "s"} generado${ok === 1 ? "" : "s"}`);
    if (fail > 0) toast.error(`${fail} fallaron`);
    setShowQuickGen(false);
    fetchTT();
  };

  const sgCat = cats.find((c) => c.category_id === sgForm.category_id);
  const maxRoundsForSg = (sgCat?.rounds) || tournament.rounds || 1;

  return (
    <>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <span className="text-sm text-[#64748B]">{teetimes.length} ronda{teetimes.length !== 1 ? "s" : ""} con tee times</span>
        {canWrite && (
          <div className="flex items-center gap-2 flex-wrap">
            {catsWithSchedule.length > 0 && (
              <Button size="sm" variant="outline" onClick={() => setShowQuickGen(true)} data-testid="gen-from-cal-btn">
                <Calendar className="w-4 h-4 mr-1" /> Generar desde calendario
              </Button>
            )}
            <Button size="sm" onClick={openGen} className="bg-[#1B4D3E] hover:bg-[#163F33] text-white" data-testid="gen-tt-btn">
              <Plus className="w-4 h-4 mr-1" /> Generar Tee Times
            </Button>
            <Button size="sm" variant="outline" onClick={openShotgun} data-testid="gen-shotgun-btn">
              <Flag className="w-4 h-4 mr-1" /> Escopetazo
            </Button>
            {cats.length > 0 && <TeeTimeCsvImport tid={tid} cats={cats} onImported={fetchTT} />}
          </div>
        )}
      </div>

      {canWrite && <NextRoundPanel tid={tid} cats={cats} onGenerated={fetchTT} />}
      {canWrite && teetimes.length > 0 && <PublishDayPanel tid={tid} teetimes={teetimes} onPublished={fetchTT} />}
      <RoundProgressBadges tid={tid} key={teetimes.length} />

      {showQuickGen && (
        <Card className="card-default mb-4" data-testid="quick-gen-panel">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-[#0F172A] text-sm">Generar tee times desde el calendario</h3>
              <button onClick={() => setShowQuickGen(false)} className="text-[#64748B]"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-xs text-[#64748B]">
              Se generará una tanda de tee times por cada ronda programada (con slots) usando el método de orden que elijas.
            </p>
            {catsWithSchedule.map((c) => (
              <div key={c.category_id} className="border border-gray-100 rounded-lg p-3 space-y-2">
                <div className="font-semibold text-[#0F172A] text-sm">{c.name}</div>
                {(c.round_schedules || []).map((r) => {
                  if ((r.slot_ids || []).length === 0) return null;
                  const key = `${c.category_id}_${r.round_num}`;
                  const sortBy = qgState[key] || (r.round_num === 1 ? "random" : "by_leaderboard");
                  return (
                    <div key={r.round_num} className="flex items-center gap-2 text-sm flex-wrap" data-testid={`qg-row-${c.category_id}-${r.round_num}`}>
                      <span className="w-12 font-bold text-[#1B4D3E] text-xs uppercase">R{r.round_num}</span>
                      <span className="w-32 text-[#64748B] text-xs capitalize">{r.date ? fmtPlayingDay(r.date) : "Sin fecha"}</span>
                      <span className="text-xs text-[#94A3B8]">{(r.slot_ids || []).length} slots</span>
                      <Select value={sortBy} onValueChange={(v) => setQgSort(c.category_id, r.round_num, v)}>
                        <SelectTrigger className="h-8 w-44 text-xs" data-testid={`qg-sort-${c.category_id}-${r.round_num}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="random">Aleatorio</SelectItem>
                          <SelectItem value="by_handicap">Por handicap index</SelectItem>
                          <SelectItem value="by_leaderboard">Por resultado anterior</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  );
                })}
              </div>
            ))}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={() => setShowQuickGen(false)}>Cancelar</Button>
              <Button size="sm" className="bg-[#1B4D3E] hover:bg-[#163F33] text-white" onClick={quickGen} data-testid="qg-run-btn">
                Generar todo
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {showShotgun && (
        <Card className="card-default mb-4" data-testid="shotgun-panel">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-[#0F172A] text-sm">Salida por escopetazo</h3>
              <button onClick={() => setShowShotgun(false)} className="text-[#64748B]"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-xs text-[#64748B]">Todos los grupos salen a la misma hora en los hoyos que elijas. Si varios grupos caen en un mismo hoyo se marcan A/B/C.</p>

            {cats.length > 0 && (
              <div className="space-y-1">
                <Label className="text-xs">Categoría</Label>
                <Select value={sgForm.category_id} onValueChange={(v) => setSgForm({ ...sgForm, category_id: v })}>
                  <SelectTrigger className="h-9" data-testid="sg-category"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                  <SelectContent>{cats.map((c) => <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}

            <div className="flex gap-3 flex-wrap items-end">
              <div className="space-y-1">
                <Label className="text-xs">Ronda</Label>
                <div className="flex gap-1">
                  {Array.from({ length: maxRoundsForSg }, (_, i) => i + 1).map((n) => (
                    <button key={n} onClick={() => setSgForm({ ...sgForm, round_num: n, sort_by: (sgForm.sort_by === "by_leaderboard" && n < 2) ? "random" : sgForm.sort_by })}
                      className={`w-8 h-8 rounded-md text-sm font-semibold ${sgForm.round_num === n ? "bg-[#1B4D3E] text-white" : "bg-gray-100 text-[#64748B]"}`} data-testid={`sg-round-${n}`}>{n}</button>
                  ))}
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Hora</Label>
                <Input type="time" value={sgForm.time} onChange={(e) => setSgForm({ ...sgForm, time: e.target.value })} className="h-9 w-28" data-testid="sg-time" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Jug/grupo</Label>
                <Select value={String(sgForm.players_per_group)} onValueChange={(v) => setSgForm({ ...sgForm, players_per_group: parseInt(v) })}>
                  <SelectTrigger className="h-9 w-20" data-testid="sg-ppg"><SelectValue /></SelectTrigger>
                  <SelectContent>{[2, 3, 4, 5].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Orden</Label>
                <Select value={sgForm.sort_by} onValueChange={(v) => setSgForm({ ...sgForm, sort_by: v })}>
                  <SelectTrigger className="h-9 w-44" data-testid="sg-sort"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="random">Aleatorio</SelectItem>
                    <SelectItem value="by_handicap">Por handicap index</SelectItem>
                    {cats.length > 0 && <SelectItem value="by_category">Por categoría</SelectItem>}
                    <SelectItem value="by_leaderboard" disabled={sgForm.round_num < 2}>Por resultado anterior</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <Label className="text-xs">Hoyos de salida ({sgForm.holes.size})</Label>
                <div className="flex gap-3">
                  <button className="text-[11px] text-[#1B4D3E] font-semibold" onClick={() => setSgForm({ ...sgForm, holes: new Set(Array.from({ length: 18 }, (_, i) => i + 1)) })} data-testid="sg-holes-all">Todos</button>
                  <button className="text-[11px] text-[#64748B] font-semibold" onClick={() => setSgForm({ ...sgForm, holes: new Set() })} data-testid="sg-holes-none">Ninguno</button>
                </div>
              </div>
              <div className="grid grid-cols-9 gap-1.5">
                {Array.from({ length: 18 }, (_, i) => i + 1).map((h) => {
                  const on = sgForm.holes.has(h);
                  return (
                    <button key={h} onClick={() => toggleSgHole(h)} data-testid={`sg-hole-${h}`}
                      className={`h-9 rounded-md text-sm font-semibold ${on ? "bg-[#1B4D3E] text-white" : "bg-gray-100 text-[#64748B]"}`}>{h}</button>
                  );
                })}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={() => setShowShotgun(false)}>Cancelar</Button>
              <Button size="sm" className="bg-[#1B4D3E] hover:bg-[#163F33] text-white" onClick={handleShotgun} data-testid="sg-run-btn">Generar escopetazo</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {showGen && (
        <Card className="card-default mb-4">
          <CardContent className="p-5" data-testid="gen-tt-form">
            {slots.length === 0 ? (
              <div className="text-center py-6 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-900 font-medium">No hay horarios del club</p>
                <p className="text-xs text-amber-700">Configura los horarios en el modulo "Tee Times" primero.</p>
              </div>
            ) : (
              <>
                {cats.length > 0 && (
                  <div className="mb-3">
                    <Label className="text-xs">Categoría</Label>
                    <Select
                      value={genForm.category_id || cats[0]?.category_id || ""}
                      onValueChange={(v) => setGenForm({ ...genForm, category_id: v })}
                    >
                      <SelectTrigger data-testid="gen-cat"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {cats.map((c) => (
                          <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-[#94A3B8] mt-1">
                      Los tee times se generan por categoría. Repite el proceso para cada una.
                    </p>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                  <div><Label className="text-xs">Ronda</Label>
                    <Select value={String(genForm.round_num)} onValueChange={(v) => {
                      const n = parseInt(v);
                      // Auto-fallback: leaderboard mode is invalid for round 1.
                      const sortBy = (genForm.sort_by === "by_leaderboard" && n < 2) ? "random" : genForm.sort_by;
                      setGenForm({ ...genForm, round_num: n, sort_by: sortBy });
                    }}>
                      <SelectTrigger data-testid="gen-round"><SelectValue /></SelectTrigger>
                      <SelectContent>{Array.from({ length: tournament.rounds }).map((_, i) => <SelectItem key={i + 1} value={String(i + 1)}>Ronda {i + 1}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">Jugadores/Grupo</Label>
                    <Select value={String(genForm.players_per_group)} onValueChange={(v) => setGenForm({ ...genForm, players_per_group: parseInt(v) })}>
                      <SelectTrigger data-testid="gen-ppg"><SelectValue /></SelectTrigger>
                      <SelectContent>{[2, 3, 4, 5].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">Ordenamiento</Label>
                    <Select value={genForm.sort_by} onValueChange={(v) => setGenForm({ ...genForm, sort_by: v })}>
                      <SelectTrigger data-testid="gen-sort"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="random">Aleatorio</SelectItem>
                        <SelectItem value="by_handicap">Por Handicap</SelectItem>
                        {!genForm.category_id && <SelectItem value="by_category">Por Categoría</SelectItem>}
                        {parseInt(genForm.round_num) >= 2 && (
                          <SelectItem value="by_leaderboard">Por Leaderboard (ronda previa)</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    {genForm.sort_by === "by_leaderboard" && (
                      <p className="text-[11px] text-[#94A3B8] mt-1">
                        Ordena por posición al cierre de la ronda {parseInt(genForm.round_num) - 1}.
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between mt-1">
                  <Label className="text-xs">Horarios a usar ({genForm.selectedSlotIds.size}/{slots.length} · capacidad {genForm.selectedSlotIds.size * genForm.players_per_group})</Label>
                  <button
                    type="button"
                    onClick={() => setGenForm((p) => ({
                      ...p,
                      selectedSlotIds: p.selectedSlotIds.size === slots.length ? new Set() : new Set(slots.map((s) => s.slot_id)),
                    }))}
                    className="text-xs font-semibold text-[#1B4D3E] hover:underline"
                    data-testid="gen-toggle-all-slots"
                  >
                    {genForm.selectedSlotIds.size === slots.length ? "Deseleccionar todos" : "Seleccionar todos"}
                  </button>
                </div>
                <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-2 grid grid-cols-2 sm:grid-cols-3 gap-1 mt-1 mb-3" data-testid="gen-slots">
                  {slots.map((s) => (
                    <label key={s.slot_id} className={`flex items-center gap-2 px-2 py-1.5 rounded text-sm cursor-pointer ${genForm.selectedSlotIds.has(s.slot_id) ? "bg-[#1B4D3E]/10" : "hover:bg-gray-50"}`}>
                      <input type="checkbox" checked={genForm.selectedSlotIds.has(s.slot_id)} onChange={() => toggleSlot(s.slot_id)} className="w-4 h-4" />
                      <span className="font-medium text-[#0F172A]">{s.time}</span>
                      <span className="text-xs text-[#64748B]">H{s.hole}</span>
                    </label>
                  ))}
                </div>
              </>
            )}
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowGen(false)}>Cancelar</Button>
              <Button size="sm" onClick={handleGen} className="bg-[#1B4D3E] hover:bg-[#163F33] text-white" disabled={slots.length === 0} data-testid="confirm-gen-btn">Generar</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex justify-center py-8"><div className="w-6 h-6 border-3 border-[#1B4D3E] border-t-transparent rounded-full animate-spin" /></div>
      ) : teetimes.length === 0 ? (
        <Card className="card-default"><CardContent className="py-12 text-center text-[#94A3B8]">Sin tee times generados</CardContent></Card>
      ) : (
        <TeeTimeList
          teetimes={teetimes}
          catFilter={catFilter} setCatFilter={setCatFilter}
          roundFilter={roundFilter} setRoundFilter={setRoundFilter}
          dayFilter={dayFilter} setDayFilter={setDayFilter}
          nameQuery={nameQuery} setNameQuery={setNameQuery}
          canWrite={canWrite}
          onPublish={handlePublish}
        />
      )}
    </>
  );
}

// Category / round / day / name filter shared between admin and public
// tee time views. Returns the visible tee times (with groups filtered by
// name query) plus derived filter options.
function useTeeTimeFilters(teetimes, catFilter, roundFilter, dayFilter, nameQuery) {
  return useMemo(() => {
    const catOpts = new Map();  // name → count
    const roundOpts = new Map(); // round_num → count
    const dayOpts = new Map();   // date → count
    for (const tt of teetimes) {
      const cn = tt.category_name || "__none__";
      catOpts.set(cn, (catOpts.get(cn) || 0) + 1);
      roundOpts.set(tt.round_num, (roundOpts.get(tt.round_num) || 0) + 1);
      const d = tt.playing_date || "__none__";
      dayOpts.set(d, (dayOpts.get(d) || 0) + 1);
    }
    const term = (nameQuery || "").trim().toLowerCase();
    const filtered = [];
    for (const tt of teetimes) {
      if (catFilter !== "all") {
        const cn = tt.category_name || "__none__";
        if (cn !== catFilter) continue;
      }
      if (roundFilter !== "all" && String(tt.round_num) !== String(roundFilter)) continue;
      if (dayFilter !== "all") {
        const d = tt.playing_date || "__none__";
        if (d !== dayFilter) continue;
      }
      if (!term) { filtered.push(tt); continue; }
      // Name search: keep tee time only if any group has a matching player.
      const groups = (tt.groups || []).filter((g) =>
        (g.players || []).some((p) => (p.name || "").toLowerCase().includes(term))
      );
      if (groups.length > 0) filtered.push({ ...tt, groups });
    }
    return {
      filtered,
      catOpts: Array.from(catOpts.entries()).map(([name, count]) => ({ name, count })),
      roundOpts: Array.from(roundOpts.keys()).sort((a, b) => a - b),
      dayOpts: Array.from(dayOpts.keys()).filter((d) => d !== "__none__").sort(),
      hasUnknownDay: dayOpts.has("__none__"),
    };
  }, [teetimes, catFilter, roundFilter, dayFilter, nameQuery]);
}

function TeeTimeList({
  teetimes, catFilter, setCatFilter, roundFilter, setRoundFilter,
  dayFilter, setDayFilter, nameQuery, setNameQuery, canWrite, onPublish,
}) {
  const { filtered, catOpts, roundOpts, dayOpts, hasUnknownDay } = useTeeTimeFilters(
    teetimes, catFilter, roundFilter, dayFilter, nameQuery,
  );
  const term = (nameQuery || "").trim().toLowerCase();
  const totalGroups = filtered.reduce((n, tt) => n + (tt.groups?.length || 0), 0);

  return (
    <div className="space-y-3" data-testid="tt-list">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm text-[#64748B]" data-testid="tt-count">
          {filtered.length} de {teetimes.length} tee time{teetimes.length === 1 ? "" : "s"}
          {term && ` · ${totalGroups} grupo${totalGroups === 1 ? "" : "s"} con coincidencias`}
        </span>
        <Input
          value={nameQuery}
          onChange={(e) => setNameQuery(e.target.value)}
          placeholder="Buscar jugador por nombre…"
          className="w-full sm:w-64"
          data-testid="tt-search"
        />
      </div>
      <TeeTimeFilterChips
        catOpts={catOpts} catFilter={catFilter} setCatFilter={setCatFilter}
        roundOpts={roundOpts} roundFilter={roundFilter} setRoundFilter={setRoundFilter}
        dayOpts={dayOpts} hasUnknownDay={hasUnknownDay}
        dayFilter={dayFilter} setDayFilter={setDayFilter}
      />
      {filtered.length === 0 ? (
        <Card className="card-default"><CardContent className="py-12 text-center text-[#94A3B8]">
          Ningún tee time coincide con los filtros
        </CardContent></Card>
      ) : filtered.map((tt) => (
        <Card key={tt.teetime_id} className="card-default" data-testid={`tt-round-${tt.round_num}`}>
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="flex items-center gap-2 flex-wrap">
                <h4 className="font-bold text-[#0F172A]">
                  Ronda {tt.round_num}
                  {tt.category_name && <span className="text-[#64748B] font-normal"> · {tt.category_name}</span>}
                </h4>
                {tt.playing_date && (
                  <span className="text-xs text-[#64748B] capitalize">{fmtPlayingDay(tt.playing_date)}</span>
                )}
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${tt.status === "published" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>{tt.status === "published" ? "Publicado" : "Borrador"}</span>
                <span className="text-xs text-[#94A3B8]">{tt.total_groups} grupos, {tt.total_players} jugadores</span>
              </div>
              {canWrite && <Button variant="outline" size="sm" onClick={() => onPublish(tt.teetime_id, tt.status)} data-testid={`publish-${tt.round_num}`}>{tt.status === "published" ? "Despublicar" : "Publicar"}</Button>}
            </div>
            <div className="space-y-2">
              {tt.groups.map((g) => (
                <div key={g.group_number} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="min-w-[60px] text-center">
                    <p className="text-xs text-[#94A3B8]">G{g.group_number}</p>
                    <p className="font-bold text-[#0F172A]">{g.time}</p>
                    <p className="text-xs text-[#94A3B8]">H{g.hole}</p>
                  </div>
                  <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-1 text-sm">
                    {g.players.map((p) => {
                      const match = term && (p.name || "").toLowerCase().includes(term);
                      return (
                        <div key={p.player_id} className={`flex items-center gap-2 rounded px-1 ${match ? "bg-amber-100" : ""}`}>
                          <span className={`text-[#0F172A] ${match ? "font-semibold" : ""}`}>{p.name}</span>
                          {p.handicap_index != null && <span className="text-xs text-[#94A3B8]">({p.handicap_index})</span>}
                          {p.category_name && <span className="text-xs bg-[#1B4D3E]/10 text-[#1B4D3E] px-1.5 py-0.5 rounded">{p.category_name}</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function TeeTimeFilterChips({
  catOpts, catFilter, setCatFilter,
  roundOpts, roundFilter, setRoundFilter,
  dayOpts, hasUnknownDay, dayFilter, setDayFilter,
}) {
  const chipCls = (active, tone = "green") =>
    `px-2.5 h-7 rounded-full text-[11px] font-semibold border transition-colors ${
      active
        ? tone === "amber"
          ? "bg-amber-500 text-white border-amber-500"
          : "bg-[#1B4D3E] text-white border-[#1B4D3E]"
        : "bg-white text-[#64748B] border-gray-200"
    }`;
  const hasCats = catOpts.length > (catOpts.some((c) => c.name === "__none__") ? 1 : 0);
  return (
    <div className="space-y-1.5">
      {hasCats && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="tt-filter-cats">
          <span className="text-[11px] font-semibold text-[#94A3B8] mr-1 uppercase tracking-wide">Categoría</span>
          <button onClick={() => setCatFilter("all")} className={chipCls(catFilter === "all")} data-testid="tt-cat-all">Todas</button>
          {catOpts.map((c) => c.name === "__none__" ? (
            <button key="none" onClick={() => setCatFilter("__none__")} className={chipCls(catFilter === "__none__", "amber")} data-testid="tt-cat-none">
              General <span className="opacity-70 ml-1">{c.count}</span>
            </button>
          ) : (
            <button key={c.name} onClick={() => setCatFilter(c.name)} className={chipCls(catFilter === c.name)} data-testid={`tt-cat-${c.name}`}>
              {c.name} <span className="opacity-70 ml-1">{c.count}</span>
            </button>
          ))}
        </div>
      )}
      {roundOpts.length > 1 && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="tt-filter-rounds">
          <span className="text-[11px] font-semibold text-[#94A3B8] mr-1 uppercase tracking-wide">Ronda</span>
          <button onClick={() => setRoundFilter("all")} className={chipCls(roundFilter === "all")} data-testid="tt-round-all">Todas</button>
          {roundOpts.map((r) => (
            <button key={r} onClick={() => setRoundFilter(String(r))} className={chipCls(String(roundFilter) === String(r))} data-testid={`tt-round-${r}`}>
              R{r}
            </button>
          ))}
        </div>
      )}
      {(dayOpts.length > 1 || (dayOpts.length === 1 && hasUnknownDay)) && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="tt-filter-days">
          <span className="text-[11px] font-semibold text-[#94A3B8] mr-1 uppercase tracking-wide">Día</span>
          <button onClick={() => setDayFilter("all")} className={chipCls(dayFilter === "all")} data-testid="tt-day-all">Todos</button>
          {dayOpts.map((d) => (
            <button key={d} onClick={() => setDayFilter(d)} className={chipCls(dayFilter === d)} data-testid={`tt-day-${d}`}>
              <span className="capitalize">{fmtPlayingDay(d)}</span>
            </button>
          ))}
          {hasUnknownDay && (
            <button onClick={() => setDayFilter("__none__")} className={chipCls(dayFilter === "__none__", "amber")} data-testid="tt-day-none">
              Sin fecha
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function LeaderboardTab({ tournament }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeCat, setActiveCat] = useState("__overall__");
  const [nameQuery, setNameQuery] = useState("");
  const [scorecardPid, setScorecardPid] = useState(null);
  const tid = tournament.tournament_id;

  useEffect(() => {
    fetch(`${API}/club/tournaments/${tid}/leaderboard`, { credentials: "include", headers: AUTH() })
      .then((r) => r.ok ? r.json() : null).then(setData).finally(() => setLoading(false));
  }, [tid]);

  const catNames = useMemo(
    () => (data ? Object.keys(data.by_category || {}) : []),
    [data],
  );

  // Default to the first category when the tournament is categorized.
  useEffect(() => {
    if (catNames.length > 0 && activeCat === "__overall__") setActiveCat(catNames[0]);
  }, [catNames, activeCat]);

  if (loading) return <div className="flex justify-center py-8"><div className="w-6 h-6 border-3 border-[#1B4D3E] border-t-transparent rounded-full animate-spin" /></div>;
  if (!data) return null;

  const globalFormat = tournament.scoring_format || "medal_play";
  const categoryFormat = (name) => {
    const c = (tournament.categories || []).find((x) => x.name === name);
    return c?.scoring_format || globalFormat;
  };
  const globalMode = tournament.mode || "gross";
  const categoryMode = (name) => {
    const c = (tournament.categories || []).find((x) => x.name === name);
    return c?.mode || globalMode;
  };

  const activeEntries = activeCat === "__overall__"
    ? (data.overall || [])
    : (data.by_category[activeCat] || []);
  const activeFormat = activeCat === "__overall__" ? globalFormat : categoryFormat(activeCat);
  const activeMode = activeCat === "__overall__" ? globalMode : categoryMode(activeCat);
  const activeTitle = activeCat === "__overall__" ? "Clasificación General" : activeCat;
  const term = nameQuery.trim().toLowerCase();
  const filtered = term
    ? activeEntries.filter((e) => (e.name || "").toLowerCase().includes(term))
    : activeEntries;

  const activeCut = activeCat === "__overall__"
    ? null
    : (() => {
        const c = (tournament.categories || []).find((x) => x.name === activeCat);
        return c?.cut && c.cut.enabled ? c.cut : null;
      })();
  const roundsPlayedMax = activeEntries.reduce((m, e) => Math.max(m, e.rounds_played || 0), 0);
  const cutPlayers = activeCut && activeCut.players > 0 && roundsPlayedMax >= activeCut.after_round
    ? activeCut.players
    : null;

  const renderTable = (entries, title, format, mode, cutPlayers) => {
    const isStable = format === "stableford";
    const isNet = mode === "net";
    const colCount = 4 + tournament.rounds + (isNet ? 1 : 0) + (isStable ? 1 : 0);
    const showCut = cutPlayers != null && !term && entries.some((e) => e.made_cut === true) && entries.some((e) => e.made_cut === false);
    const cutAt = showCut ? entries.findIndex((e) => e.made_cut === false) : -1;
    return (
    <Card key={title} className="card-default">
      <CardContent className="p-0">
        <div className="bg-[#1B4D3E]/5 px-4 py-2.5 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-bold text-sm text-[#1B4D3E]">{title}</h3>
          <span className="text-[10px] uppercase tracking-wider font-bold text-[#1B4D3E]/70">
            {isStable ? "Stableford" : "Medal Play"}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="lb-table">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-center px-3 py-2 text-xs font-semibold text-[#64748B] w-12">POS</th>
                <th className="text-left px-3 py-2 text-xs font-semibold text-[#64748B]">JUGADOR</th>
                <th className="text-center px-3 py-2 text-xs font-semibold text-[#64748B]">IDX</th>
                {Array.from({ length: tournament.rounds }).map((_, i) => (
                  <th key={i} className="text-center px-3 py-2 text-xs font-semibold text-[#64748B]">R{i + 1}</th>
                ))}
                <th className="text-center px-3 py-2 text-xs font-semibold text-[#0F172A]">GROSS</th>
                {isNet && <th className="text-center px-3 py-2 text-xs font-semibold text-[#1B4D3E]">NET</th>}
                {isStable && <th className="text-center px-3 py-2 text-xs font-semibold text-[#1B4D3E]">PTS</th>}
                <th className="text-center px-3 py-2 text-xs font-semibold text-[#64748B]">vs PAR</th>
              </tr>
            </thead>
            <tbody>
              {entries.length === 0 ? (
                <tr><td colSpan={7 + tournament.rounds} className="text-center py-6 text-[#94A3B8]">
                  {term ? "Ningún jugador coincide con la búsqueda" : "Sin resultados aun"}
                </td></tr>
              ) : entries.flatMap((e, idx) => {
                const missed = cutAt !== -1 && idx >= cutAt;
                const rowCls = e.result_status
                  ? "bg-gray-50/60"
                  : e.rounds_played === 0
                  ? "opacity-40"
                  : missed
                  ? "opacity-50 bg-gray-50/40 text-[#94A3B8]"
                  : (idx < 3 && !term ? "bg-amber-50/30" : "");
                const rows = [];
                if (showCut && cutAt > 0 && idx === cutAt) {
                  rows.push(
                    <tr key="cut-line" className="bg-red-50/80 border-t-2 border-red-400" data-testid="lb-cut-line">
                      <td colSpan={colCount} className="px-3 py-1.5 text-center">
                        <span className="inline-flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-widest text-red-600">
                          <span className="h-px w-8 bg-red-400" />
                          Línea de Corte · Pasan {cutPlayers}
                          <span className="h-px w-8 bg-red-400" />
                        </span>
                      </td>
                    </tr>
                  );
                }
                rows.push(
                <tr key={e.player_id} className={`border-t border-gray-50 ${rowCls}`} data-testid={`lb-row-${e.player_id}`}>
                  <td className="text-center px-3 py-2 font-bold">
                    {e.result_status ? "—" : (e.position ?? "—")}
                    {!e.result_status && e.tiebreak && <span className="block text-[9px] font-semibold text-amber-600 leading-none" title="Resuelto por desempate (countback)">c/b</span>}
                  </td>
                  <td className="px-3 py-2 font-medium text-[#0F172A]">
                    <button type="button" onClick={() => setScorecardPid(e.player_id)} className="text-left hover:text-[#1B4D3E] hover:underline" data-testid={`lb-name-${e.player_id}`}>{e.name}</button>
                    {e.result_status && <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded font-bold ${e.result_status === "DQ" ? "bg-red-600 text-white" : "bg-amber-500 text-white"}`}>{e.result_status}</span>}
                    {missed && !e.result_status && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded font-bold bg-gray-300 text-gray-700">CUT</span>}
                  </td>
                  <td className="text-center px-3 py-2 text-[#64748B]">{e.handicap_index ?? "—"}</td>
                  {Array.from({ length: tournament.rounds }).map((_, i) => (
                    <td key={i} className="text-center px-3 py-2 text-[#64748B]">
                      {isStable ? ((e.stableford_rounds || [])[i] ?? "—") : (isNet ? (e.net_rounds[i] ?? "—") : (e.gross_rounds[i] ?? "—"))}
                    </td>
                  ))}
                  <td className="text-center px-3 py-2 font-bold text-[#0F172A]">{e.result_status ? "—" : (e.rounds_played > 0 ? e.gross_total : "—")}</td>
                  {isNet && <td className="text-center px-3 py-2 font-bold text-[#1B4D3E]">{e.result_status ? "—" : (e.rounds_played > 0 ? e.net_total : "—")}</td>}
                  {isStable && <td className="text-center px-3 py-2 font-bold text-[#1B4D3E]">{e.result_status ? "—" : (e.rounds_played > 0 ? e.stableford_total : "—")}</td>}
                  <td className="text-center px-3 py-2 text-[#64748B]">{e.result_status ? "—" : (e.rounds_played > 0 ? formatToPar(isNet ? e.score_to_par_net : e.score_to_par_gross) : "—")}</td>
                </tr>
                );
                return rows;
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
    );
  };

  const chipCls = (active) =>
    `px-3 h-8 rounded-full text-xs font-semibold border transition-colors ${
      active ? "bg-[#1B4D3E] text-white border-[#1B4D3E]" : "bg-white text-[#64748B] border-gray-200"
    }`;

  return (
    <div className="space-y-3" data-testid="leaderboard-tab">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm text-[#64748B]" data-testid="lb-count">
          {filtered.length} de {activeEntries.length} jugador{activeEntries.length === 1 ? "" : "es"}
        </span>
        <Input
          value={nameQuery}
          onChange={(e) => setNameQuery(e.target.value)}
          placeholder="Buscar jugador por nombre…"
          className="w-full sm:w-64"
          data-testid="lb-search"
        />
      </div>
      {(catNames.length > 0 || data.overall?.length > 0) && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="lb-cat-filter">
          {catNames.length === 0 || data.overall?.length > 0 ? (
            <button onClick={() => setActiveCat("__overall__")} className={chipCls(activeCat === "__overall__")} data-testid="lb-cat-overall">
              General <span className="opacity-70 ml-1">{data.overall?.length || 0}</span>
            </button>
          ) : null}
          {catNames.map((cn) => (
            <button key={cn} onClick={() => setActiveCat(cn)} className={chipCls(activeCat === cn)} data-testid={`lb-cat-${cn}`}>
              {cn} <span className="opacity-70 ml-1">{data.by_category[cn]?.length || 0}</span>
            </button>
          ))}
        </div>
      )}
      {renderTable(filtered, activeTitle, activeFormat, activeMode, cutPlayers)}
      <ScorecardModal
        open={!!scorecardPid}
        onClose={() => setScorecardPid(null)}
        url={scorecardPid ? `${API}/club/tournaments/${tid}/players/${scorecardPid}/scorecard` : null}
        headers={AUTH()}
      />
    </div>
  );
}

function formatToPar(v) {
  if (v === 0) return "E";
  return v > 0 ? `+${v}` : `${v}`;
}

// ==========================================================================
// Events / Calendar Tab
// ==========================================================================

const EVENT_ICONS = {
  golf: { icon: Award, color: "text-green-600 bg-green-50" },
  food: { icon: Utensils, color: "text-orange-600 bg-orange-50" },
  ceremony: { icon: Trophy, color: "text-amber-600 bg-amber-50" },
  transport: { icon: Car, color: "text-blue-600 bg-blue-50" },
  registration: { icon: ClipboardList, color: "text-purple-600 bg-purple-50" },
  party: { icon: PartyPopper, color: "text-pink-600 bg-pink-50" },
  info: { icon: Info, color: "text-gray-600 bg-gray-50" },
};

function EventsTab({ tournament, canWrite }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);  // null | "new" | event object
  const tid = tournament.tournament_id;

  const fetchEvents = useCallback(async () => {
    try {
      const res = await fetch(`${API}/club/tournaments/${tid}/events`, { credentials: "include", headers: AUTH() });
      if (res.ok) setEvents(await res.json());
    } finally { setLoading(false); }
  }, [tid]);
  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const del = async (eid) => {
    if (!window.confirm("Eliminar evento?")) return;
    try {
      await fetch(`${API}/club/tournaments/${tid}/events/${eid}`, { method: "DELETE", credentials: "include", headers: AUTH() });
      toast.success("Eliminado"); fetchEvents();
    } catch { toast.error("Error"); }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm text-[#64748B]">{events.length} evento{events.length !== 1 ? "s" : ""}</span>
        {canWrite && <Button size="sm" onClick={() => setEditing("new")} className="bg-[#1B4D3E] hover:bg-[#163F33] text-white" data-testid="add-event-btn"><Plus className="w-4 h-4 mr-1" /> Agregar Evento</Button>}
      </div>

      {editing && <EventForm tournament={tournament} event={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); fetchEvents(); }} />}

      {loading ? (
        <div className="flex justify-center py-8"><div className="w-6 h-6 border-3 border-[#1B4D3E] border-t-transparent rounded-full animate-spin" /></div>
      ) : events.length === 0 ? (
        <Card className="card-default"><CardContent className="py-12 text-center text-[#94A3B8]">Sin eventos en el calendario</CardContent></Card>
      ) : (
        <div className="space-y-3" data-testid="events-list">
          {events.map((e) => {
            const cfg = EVENT_ICONS[e.icon] || EVENT_ICONS.info;
            const Icon = cfg.icon;
            const dt = e.datetime ? new Date(e.datetime) : null;
            return (
              <Card key={e.event_id} className="card-default" data-testid={`event-${e.event_id}`}>
                <CardContent className="p-4 flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${cfg.color}`}>
                    <Icon className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-bold text-[#0F172A]">{e.title}</h4>
                    {dt && (
                      <p className="text-xs text-[#1B4D3E] font-medium mt-0.5">
                        {dt.toLocaleDateString("es", { weekday: "short", day: "numeric", month: "short" })} · {dt.toLocaleTimeString("es", { hour: "2-digit", minute: "2-digit" })}
                      </p>
                    )}
                    {e.location && <p className="text-xs text-[#64748B] mt-0.5 flex items-center gap-1"><MapPin className="w-3 h-3" /> {e.location}</p>}
                    {e.description && <p className="text-sm text-[#64748B] mt-1">{e.description}</p>}
                    {e.attachment_url && (
                      <a href={e.attachment_url} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 mt-2 text-xs text-[#1B4D3E] hover:underline" data-testid={`event-attachment-${e.event_id}`}>
                        <FileText className="w-3 h-3" /> {e.attachment_name || "Ver archivo"}
                      </a>
                    )}
                  </div>
                  {canWrite && (
                    <div className="flex flex-col gap-1">
                      <button onClick={() => setEditing(e)} className="text-[#64748B] hover:text-[#1B4D3E]" data-testid={`edit-event-${e.event_id}`}><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => del(e.event_id)} className="text-red-500 hover:text-red-700" data-testid={`del-event-${e.event_id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </>
  );
}

function EventForm({ tournament, event, onClose, onSaved }) {
  const tid = tournament.tournament_id;
  const [form, setForm] = useState({
    title: event?.title || "",
    description: event?.description || "",
    datetime: event?.datetime || "",
    location: event?.location || "",
    icon: event?.icon || "info",
    attachment_url: event?.attachment_url || "",
    attachment_name: event?.attachment_name || "",
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/club/tournaments/${tid}/uploads`, {
        method: "POST", credentials: "include",
        headers: { Authorization: `Bearer ${localStorage.getItem("cadii_club_token") || ""}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Error");
      setForm((p) => ({ ...p, attachment_url: data.url, attachment_name: data.name }));
      toast.success("Archivo subido");
    } catch (err) { toast.error(err.message || "Error"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Titulo requerido");
    setSaving(true);
    try {
      const url = event ? `${API}/club/tournaments/${tid}/events/${event.event_id}` : `${API}/club/tournaments/${tid}/events`;
      const method = event ? "PUT" : "POST";
      const res = await fetch(url, {
        method, credentials: "include", headers: AUTH(),
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Error");
      toast.success(event ? "Actualizado" : "Creado");
      onSaved();
    } catch (err) { toast.error(err.message || "Error"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="event-form">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white">
          <h3 className="font-bold text-[#0F172A]">{event ? "Editar Evento" : "Nuevo Evento"}</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-[#64748B]" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div><Label className="text-sm font-medium">Titulo *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ej: Inauguracion" data-testid="event-title" /></div>
          <div><Label className="text-sm font-medium">Fecha y Hora</Label><Input type="datetime-local" value={form.datetime} onChange={(e) => setForm({ ...form, datetime: e.target.value })} data-testid="event-datetime" /></div>
          <div><Label className="text-sm font-medium">Ubicacion</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Tee 1 / Casa Club / etc." data-testid="event-location" /></div>
          <div><Label className="text-sm font-medium">Descripcion</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Detalles..." data-testid="event-desc" /></div>
          <div>
            <Label className="text-sm font-medium">Tipo</Label>
            <div className="grid grid-cols-4 sm:grid-cols-7 gap-2 mt-1.5" data-testid="event-icons">
              {Object.entries(EVENT_ICONS).map(([key, cfg]) => {
                const Icon = cfg.icon;
                return (
                  <button key={key} type="button" onClick={() => setForm({ ...form, icon: key })}
                    className={`flex flex-col items-center gap-1 p-2 rounded-lg border-2 transition-colors ${form.icon === key ? "border-[#1B4D3E] bg-[#1B4D3E]/5" : "border-gray-200"}`}
                    data-testid={`event-icon-${key}`}>
                    <div className={`w-7 h-7 rounded flex items-center justify-center ${cfg.color}`}>
                      <Icon className="w-4 h-4" strokeWidth={1.5} />
                    </div>
                    <span className="text-[10px] text-[#64748B] capitalize">{key}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <Label className="text-sm font-medium">Adjunto (opcional)</Label>
            {form.attachment_url ? (
              <div className="flex items-center gap-2 p-2.5 bg-gray-50 rounded-lg mt-1.5">
                <FileText className="w-4 h-4 text-[#1B4D3E]" />
                <span className="text-sm text-[#0F172A] flex-1 truncate">{form.attachment_name}</span>
                <button onClick={() => setForm({ ...form, attachment_url: "", attachment_name: "" })} className="text-red-500" data-testid="clear-attachment-btn"><X className="w-4 h-4" /></button>
              </div>
            ) : (
              <label className="mt-1.5 flex items-center gap-2 p-3 border-2 border-dashed border-gray-200 rounded-lg cursor-pointer hover:border-[#1B4D3E] transition-colors" data-testid="event-attachment-label">
                <Upload className="w-4 h-4 text-[#64748B]" />
                <span className="text-sm text-[#64748B]">{uploading ? "Subiendo..." : "Subir archivo (PDF o imagen, hasta 15MB)"}</span>
                <input type="file" className="hidden" onChange={onFile} disabled={uploading} accept="image/*,application/pdf" data-testid="event-attachment-input" />
              </label>
            )}
          </div>
        </div>
        <div className="px-6 py-4 border-t border-gray-100 flex gap-2 sticky bottom-0 bg-white">
          <Button variant="outline" className="flex-1" onClick={onClose}>Cancelar</Button>
          <Button className="flex-1 bg-[#1B4D3E] hover:bg-[#163F33] text-white" onClick={save} disabled={saving} data-testid="event-save-btn">{saving ? "..." : "Guardar"}</Button>
        </div>
      </div>
    </div>
  );
}

// ==========================================================================
// Gallery Tab
// ==========================================================================

function GalleryTab({ tournament, canWrite }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const tid = tournament.tournament_id;

  const fetchPhotos = useCallback(async () => {
    try {
      const res = await fetch(`${API}/club/tournaments/${tid}/photos`, { credentials: "include", headers: AUTH() });
      if (res.ok) setPhotos(await res.json());
    } finally { setLoading(false); }
  }, [tid]);
  useEffect(() => { fetchPhotos(); }, [fetchPhotos]);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("caption", "");
        const res = await fetch(`${API}/club/tournaments/${tid}/photos`, {
          method: "POST", credentials: "include",
          headers: { Authorization: `Bearer ${localStorage.getItem("cadii_club_token") || ""}` },
          body: fd,
        });
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.detail || "Error en " + file.name);
        }
      }
      toast.success(`${files.length} foto${files.length > 1 ? "s" : ""} subida${files.length > 1 ? "s" : ""}`);
      fetchPhotos();
    } catch (err) {
      toast.error(err.message || "Error subiendo");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const del = async (pid) => {
    if (!window.confirm("Eliminar foto?")) return;
    try {
      await fetch(`${API}/club/tournaments/${tid}/photos/${pid}`, { method: "DELETE", credentials: "include", headers: AUTH() });
      toast.success("Eliminada"); fetchPhotos();
    } catch { toast.error("Error"); }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm text-[#64748B]">{photos.length} foto{photos.length !== 1 ? "s" : ""}</span>
        {canWrite && (
          <label className="cursor-pointer" data-testid="upload-photo-label">
            <span className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#1B4D3E] hover:bg-[#163F33] text-white text-sm font-medium">
              <Upload className="w-4 h-4" /> {uploading ? "Subiendo..." : "Subir Fotos"}
            </span>
            <input type="file" className="hidden" multiple accept="image/jpeg,image/png,image/webp,image/heic,image/heif" onChange={onUpload} disabled={uploading} data-testid="upload-photo-input" />
          </label>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><div className="w-6 h-6 border-3 border-[#1B4D3E] border-t-transparent rounded-full animate-spin" /></div>
      ) : photos.length === 0 ? (
        <Card className="card-default"><CardContent className="py-12 text-center">
          <ImageIcon className="w-12 h-12 text-[#64748B] mx-auto mb-3 opacity-30" strokeWidth={1.5} />
          <p className="text-[#94A3B8]">Sin fotos en la galeria</p>
          <p className="text-xs text-[#CBD5E1] mt-1">Hasta 10MB por imagen · JPG, PNG, WebP, HEIC</p>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2" data-testid="photos-grid">
          {photos.map((p) => (
            <div key={p.photo_id} className="relative aspect-square group" data-testid={`photo-${p.photo_id}`}>
              <button onClick={() => setLightbox(p)} className="w-full h-full overflow-hidden rounded-lg bg-gray-100 block">
                <img src={p.url} alt={p.caption || ""} className="w-full h-full object-cover hover:scale-105 transition-transform" loading="lazy" />
              </button>
              {canWrite && (
                <button onClick={() => del(p.photo_id)} className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-red-500/90 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center" data-testid={`del-photo-${p.photo_id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="lightbox">
          <button onClick={() => setLightbox(null)} className="absolute top-4 right-4 text-white" data-testid="lightbox-close"><X className="w-6 h-6" /></button>
          <img src={lightbox.url} alt={lightbox.caption || ""} className="max-w-full max-h-full object-contain" />
        </div>
      )}
    </>
  );
}
